import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Container as ContainerIcon,
  Download,
  Eye,
  FileText,
  Loader2,
  ListChecks,
  Lock,
  Map as MapIcon,
  Milestone,
  PencilLine,
  Stamp,
  Plus,
  Save,
  Ship,
  StickyNote,
  Truck,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { SectionCard } from "../../components/UI/SectionCard";
import { FormField, Input, Select, Textarea } from "../../components/UI/FormField";
import Button from "../../components/UI/Button";
import { InfoTip } from "../../components/UI/InfoTip";
import { useConfirm } from "../../hooks/useConfirm";
import {
  Field,
  FieldGrid,
} from "../../components/ContainerOrderForm/readonlyFields";
import { ContainerLineReadonly } from "../../components/ContainerOrderForm/ContainerLineReadonly";
import { ContainerLineTimeline } from "../../components/ContainerOrderForm/ContainerLineTimeline";
import { ContainerLineTimelinePopover } from "../../components/ContainerOrderForm/ContainerLineTimelinePopover";
import { ContainerLineCargoSection } from "../../components/ContainerOrderForm/ContainerLineCargoSection";
import { ContainerLineStopCard } from "../../components/ContainerOrderForm/ContainerLineStopCard";
import { ContainerLineAttachmentsSection } from "../../components/ContainerOrderForm/ContainerLineAttachmentsSection";
import { ContainerMessagesSection } from "../../components/ContainerOrderForm/ContainerMessagesSection";
import { AssignmentSelect } from "../../components/directory/AssignmentSelect";
import HandlerAssign from "../../components/handler/HandlerAssign";
import { ServicesSection } from "../../components/ShipmentForm/ServicesSection";
import { CostServicesSection } from "../../components/ShipmentForm/CostServicesSection";
import InvoiceRefs from "../../components/invoices/InvoiceRefs";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  fetchContainerLine,
  updateContainerLine,
} from "../../store/slices/containerOrdersSlice";
import { getCountries } from "../../store/slices/companySlice";
import { getServices } from "../../store/slices/servicesSlice";
import { getCostServices } from "../../store/slices/costServicesSlice";
import { withVersion } from "../../utils/concurrency";
import {
  checkContainerNumber,
  containerNumberMessage,
} from "../../utils/containerNumber";
import { translateValidation } from "../../utils/translateValidation";
import {
  cargoTotalWeight,
  cargoIssueOf,
  deriveVgm,
  effectiveCargoKg,
  evaluateWeights,
  findCargoRowErrors,
  fromCargoRows,
  issueFor,
  toCargoRows,
} from "../../utils/containerLineCargo";
import type { CargoRowDraft } from "../../utils/containerLineCargo";
import {
  dayOf,
  findRouteSequenceViolations,
  timeOf,
} from "../../utils/routeSequence";
import {
  findAllStopIssues,
  firstStopIssue,
} from "../../utils/containerLineStops";
import type { StopIssues } from "../../utils/containerLineStops";
import {
  canMoveStop,
  isStopBehindDriver,
  isStopVisited,
  makeCustomStop,
  moveStop,
  routeStopsPayload,
  STOP_SLOT_KEYS,
} from "../../utils/containerRoute";
import {
  isFieldFrozen,
  hasStarted,
  blockingRequirements,
  hasLeftFirstStop,
  missingLineStartRequirements,
} from "../../utils/containerLineStart";
import type {
  LineStartField,
  LineLockableField,
  LineStartRequirement,
} from "../../utils/containerLineStart";
import {
  CONTAINER_TYPE_OPTIONS,
  CONTAINER_LINE_STATUS_STYLES,
  CONTAINER_LOAD_STATUS_VALUES,
} from "../../constants/containerOrderConstants";
import { IMO_CLASS_OPTIONS } from "../../constants/shipmentFormConstants";
import { containerOrderAPI } from "../../services/api";
import { deliverAttachment } from "../../utils/containerAttachments";
import { CmrPreview } from "../../components/shipments/CMRPreview";
import {
  buildContainerCmrOrder,
  downloadContainerCmrPdf,
} from "../../utils/containerCmr";
import type { ServiceSnapshot } from "../../types";
import type {
  AttachmentCategory,
  ContainerLineAttachment,
  ContainerLineFormData,
  ContainerLineOrderRef,
  ContainerLineStatus,
  ContainerLoadStatus,
  ContainerOrderLine,
  ContainerRouteStop,
  ContainerStopRole,
  ContainerType,
} from "../../types/containerOrders";
import { MAX_ROUTE_STOPS } from "../../types/containerOrders";
import {
  completedStepCount,
  derivedLineStatus,
  isLinePlanned,
  lineSteps,
  nextLineStatus,
  routeStopsOf,
  statusAfterStep,
} from "../../utils/containerLineSteps";
import { vehicleLabel } from "../../utils/vehicleLabel";

const GOOGLE_PLACES_API_KEY =
  import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

/** Everything the form holds as text, so a half-typed number stays half-typed. */
interface Draft {
  containerNumber: string;
  containerType: string;
  isoCode: string;
  tareKg: string;
  maxPayloadKg: string;
  /**
   * What the load weighs, as typed. `""` is "not weighed", which hands the
   * answer back to the packing list rather than declaring nothing.
   */
  cargoWeightKg: string;
  sealNumber: string;
  /** Loaded or empty. `""` is "not stated", which is a real answer here. */
  loadStatus: string;
  /** The customs MRN, free text — see the field's note in the line model. */
  mrnNumber: string;
  /**
   * Who has the box — displayed, never edited.
   *
   * Kept in the draft alongside the editable fields because the form renders it,
   * but nothing writes to either: the id is here only so the label has something
   * to be derived from, and neither is sent on save. The API strips a `driverId`
   * off any update it receives.
   */
  driverId: string | null;
  driverLabel: string;
  vehicleId: string | null;
  vehicleLabel: string;
  /** The truck's plate, for the CMR preview — `vehicleLabel` may be its fleet name. */
  vehiclePlate: string;
  shippingLine: string;
  vesselName: string;
  voyageNumber: string;
  blNumber: string;
  bookingRef: string;
  cutOffDate: string;
  cutOffTime: string;
  notes: string;
  cargo: CargoRowDraft[];
  /**
   * How this box has to be handled.
   *
   * Held as three flags and three strings — the same pairing the order form and
   * the shipment form use — so the detail beside a flag is a plain text box and
   * a half-typed temperature range stays half-typed, like everything else here.
   */
  handling: {
    temperatureSensitive: boolean;
    temperatureRange: string;
    adr: boolean;
    imoClass: string;
    fragile: boolean;
    specifications: string;
  };
  /**
   * The whole route, in the order it is driven.
   *
   * One array rather than three named stops, because the route is now the thing
   * being edited: a stop can be added between any two, moved, or marked as not
   * being used, and none of that can be expressed by three fixed fields. The
   * container's own three anchors are still in here — they are simply three of
   * the stops rather than all of them.
   */
  routeStops: ContainerRouteStop[];
}

/**
 * Which of the order form's four named slots each anchor is.
 *
 * Only the anchors have one: the six purpose-written "X is before Y" sentences
 * are about the three-stop route, and an added stop — of which there can be any
 * number — is described by its own label in the generic sentence instead.
 */
const ANCHOR_SLOT_NAMES: Partial<Record<ContainerStopRole, string>> = {
  terminalPickup: "pickup",
  loadingSite: "onSite",
  terminalDropoff: "delivery",
};

/** What a stop is called, in a sentence and on its card. */
const stopTitle = (
  stop: ContainerRouteStop,
  t: (key: string) => string,
): string =>
  stop.role === "custom"
    ? stop.label?.trim() ||
      stop.companyName?.trim() ||
      t(`route.stopKinds.${stop.kind ?? "other"}`)
    : t(`route.${stop.role}`);

/** A stop's message keys, turned into the sentences the card shows. */
const translateIssues = (issues: StopIssues): StopIssues =>
  Object.fromEntries(
    Object.entries(issues).map(([field, key]) => [
      field,
      translateValidation(key),
    ]),
  );

const str = (v: string | undefined | null) => v ?? "";
const numStr = (v: number | null | undefined) =>
  typeof v === "number" ? String(v) : "";

/** `yyyy-mm-dd` for a date input, from whatever the API returned. */
const dateInput = (v: string | null | undefined) =>
  v ? new Date(v).toISOString().slice(0, 10) : "";

const named = (ref: ContainerOrderLine["driverId"]) =>
  ref && typeof ref === "object"
    ? `${ref.firstName ?? ""} ${ref.lastName ?? ""}`.trim()
    : "";

const idOf = (ref: { _id: string } | string | null | undefined) =>
  ref ? (typeof ref === "object" ? ref._id : ref) : null;

/** A populated country ref comes back as an object; the form wants the id. */
const countryId = (country: unknown): string => {
  if (!country) return "";
  if (typeof country === "string") return country;
  return (country as { _id?: string })._id ?? "";
};

const toStopDraft = (stop: ContainerRouteStop): ContainerRouteStop => ({
  _id: stop._id,
  role: stop.role,
  kind: stop.kind,
  label: str(stop.label),
  comment: str(stop.comment),
  // Absent on stops stored before they could be turned off, and those are on.
  enabled: stop.enabled !== false,
  skipReason: str(stop.skipReason),
  // The driver's own stamps ride along untouched: the form never sends them and
  // the server puts them back regardless, but the cards read them to decide
  // which stops are still the dispatcher's to rearrange.
  arrivedAt: stop.arrivedAt ?? null,
  completedAt: stop.completedAt ?? null,
  companyName: str(stop?.companyName),
  addressId: stop?.addressId ?? null,
  partnerId: stop?.partnerId ?? null,
  address: {
    street: str(stop?.address?.street),
    city: str(stop?.address?.city),
    state: str(stop?.address?.state),
    postalCode: str(stop?.address?.postalCode),
    country: countryId(stop?.address?.country),
    // Records written before coordinates existed store a null pair; that has to
    // read as "no location", not as a location of nowhere.
    coordinates:
      typeof stop?.address?.coordinates?.latitude === "number" &&
      typeof stop?.address?.coordinates?.longitude === "number"
        ? stop.address.coordinates
        : null,
  },
  contactName: str(stop?.contactName),
  contactPhone: str(stop?.contactPhone),
  contactEmail: str(stop?.contactEmail),
  ramp: str(stop?.ramp),
  bookingRef: str(stop?.bookingRef),
  pin: str(stop?.pin),
  date: dateInput(stop?.date),
  time: str(stop?.time),
});

function toDraft(line: ContainerOrderLine): Draft {
  return {
    containerNumber: str(line.containerNumber),
    containerType: str(line.containerType),
    isoCode: str(line.isoCode),
    tareKg: numStr(line.tareKg),
    maxPayloadKg: numStr(line.maxPayloadKg),
    cargoWeightKg: numStr(line.cargoWeightKg),
    sealNumber: str(line.sealNumber),
    loadStatus: str(line.loadStatus),
    mrnNumber: str(line.mrnNumber),
    driverId: idOf(line.driverId),
    driverLabel: named(line.driverId),
    vehicleId: idOf(line.vehicleId),
    vehicleLabel:
      line.vehicleId && typeof line.vehicleId === "object"
        ? vehicleLabel(line.vehicleId)
        : "",
    vehiclePlate:
      line.vehicleId && typeof line.vehicleId === "object"
        ? str(line.vehicleId.registrationNumber)
        : "",
    shippingLine: str(line.shippingLine),
    vesselName: str(line.vesselName),
    voyageNumber: str(line.voyageNumber),
    blNumber: str(line.blNumber),
    bookingRef: str(line.bookingRef),
    cutOffDate: dateInput(line.cutOffDate),
    cutOffTime: str(line.cutOffTime),
    notes: str(line.notes),
    cargo: toCargoRows(line),
    handling: {
      temperatureSensitive: !!line.handling?.temperatureSensitive,
      temperatureRange: str(line.handling?.temperatureRange),
      adr: !!line.handling?.adr,
      imoClass: str(line.handling?.imoClass),
      fragile: !!line.handling?.fragile,
      specifications: str(line.handling?.specifications),
    },
    routeStops: routeStopsOf(line).map(toStopDraft),
  };
}

/**
 * The container's charges, as the shared service tables want them.
 *
 * Those tables are react-hook-form components — they read and write field paths
 * like `services.3.price` — while the rest of this page is a hand-held draft of
 * strings. Rather than reimplement the catalogue picker, the matrix pricing, the
 * VAT column and the totals, the page runs a small second form holding nothing
 * but these two lists, and folds its values into the payload on save.
 *
 * The names are not incidental: `services` and `costServices` are the paths
 * those components address, so this shape is the contract with them.
 */
interface ServicesDraft {
  services: ServiceSnapshot[];
  costServices: ServiceSnapshot[];
}

const toServicesDraft = (line: ContainerOrderLine): ServicesDraft => ({
  services: (line.services ?? []).map((row) => ({ ...row })),
  costServices: (line.costServices ?? []).map((row) => ({ ...row })),
});

/**
 * A charge as the API accepts it: the row, minus the parts only the server sets.
 *
 * The form seeds its rows from the stored ones by spreading them whole, which
 * is what keeps a charge's identity through an edit — but it also picks up the
 * three fields no client may write. `invoiceId` and `invoicedAt` are the
 * invoicing system's, and `orderServiceId` is the thread back to the container
 * order's price list: it decides whether an order edit may rewrite this row, so
 * a client that could set it could hand its own charge the batch's identity, or
 * take it away from one.
 *
 * All three are put back from storage on save, so dropping them here loses
 * nothing. The same reasoning the VGM and the driver are left out of the
 * payload for, one field set over — see `payloadData` below.
 */
const forSave = (rows: ServiceSnapshot[]): ServiceSnapshot[] =>
  rows.map(
    ({
      orderServiceId: _batchLink,
      invoiceId: _billed,
      invoicedAt: _billedAt,
      ...row
    }) => row,
  );

/**
 * An empty box is not the same as an untouched one: "" clears the field on the
 * server, `undefined` would leave whatever is stored. Everything on this form
 * is submitted, so a cleared field genuinely clears.
 */
const numOrNull = (value: string) =>
  value.trim() === "" ? null : Number(value);

/**
 * One container, on its own page.
 *
 * Was a modal, and outgrew it: a container now carries its whole three-stop
 * route — every stop's reference, slot, address, contact and gate PIN — on top
 * of the box's own identity, its packing list, and who is driving it. That is a
 * page's worth of form, and it is also a thing worth having a URL: a dispatcher
 * sends "the container that is late" to a colleague, and a driver's query is
 * about one box, not about the batch it happens to belong to.
 *
 * The parent order is shown read-only at the top. It is the container's
 * provenance — which booking this box belongs to — and nothing about it is this
 * page's to change; the link goes back to the order for that.
 */
export function ContainerLinePage() {
  const { t } = useTranslation(["containerOrders", "common", "shipments"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orderId = "", containerId = "" } = useParams();

  const { countries } = useAppSelector((state) => state.company);
  // The two catalogues the service tables pick from — the same lists the order
  // form and the shipment form offer.
  const { services: serviceCatalogue } = useAppSelector(
    (state) => state.services,
  );
  const { costServices: costServiceCatalogue } = useAppSelector(
    (state) => state.costServices,
  );

  const [line, setLine] = useState<ContainerOrderLine | null>(null);
  const [order, setOrder] = useState<ContainerLineOrderRef | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  // Flipped by a rejected save, so the cargo table stays quiet while a row is
  // still being typed and only goes red once Save has actually been pressed.
  const [showCargoErrors, setShowCargoErrors] = useState(false);
  /**
   * The same, for the three stops.
   *
   * Separate from the cargo flag because they are refused for different reasons
   * and a user fixing one should not be shouted at about the other. Both stay on
   * once raised: from that point the form is being corrected, and a complaint
   * that vanishes while the box is still wrong is worse than one that stays.
   */
  const [showStopErrors, setShowStopErrors] = useState(false);

  /**
   * Documents, in three pieces: what is stored, and what is waiting to go up in
   * each of the two drop zones.
   *
   * The pending files sit outside `draft` on purpose. Everything in the draft is
   * text being edited and is replaced wholesale when the server answers; these
   * are picked files that exist only until the save that consumes them, and the
   * stored list they join is written by the server, never by this form.
   */
  const [attachments, setAttachments] = useState<ContainerLineAttachment[]>([]);
  const [pendingGeneral, setPendingGeneral] = useState<File[]>([]);
  const [pendingCustoms, setPendingCustoms] = useState<File[]>([]);
  const [busyAttachment, setBusyAttachment] = useState<string | null>(null);

  /**
   * The step this save is being asked to record, or null for "leave it alone".
   *
   * Kept out of `draft` deliberately. Everything in the draft is a field whose
   * value the form owns and resends on every save; this is a one-shot request
   * to make something happen, and it must not survive the save that consumed
   * it — a status left lying in the draft would be re-sent by the next
   * unrelated edit and refused as a second advance.
   */
  const [statusTarget, setStatusTarget] = useState<ContainerLineStatus | null>(
    null,
  );

  const authUser = useAppSelector((state) => state.auth.user);
  const { confirm, confirmDialog } = useConfirm();

  /**
   * ── The charges, in a form of their own ──────────────────────────────────
   *
   * A second `useForm` on a page that otherwise holds its own draft, and it
   * earns its keep: `ServicesSection` and `CostServicesSection` are the tables
   * the shipment form and the order form already use — catalogue picker, matrix
   * zone/band pricing, VAT column, per-row and grand totals, the invoiced-row
   * lock — and every one of those is addressed through react-hook-form field
   * paths. Reimplementing them against a `useState` draft would be several
   * hundred lines of the same behaviour, diverging from the day it shipped.
   *
   * It stays strictly scoped to the two lists. Nothing else on this page goes
   * through it, and its values are folded into the one payload on save, so
   * there is still exactly one Save and one request.
   */
  const chargesForm = useForm<ServicesDraft>({
    defaultValues: { services: [], costServices: [] },
  });

  const {
    fields: serviceFields,
    append: appendService,
    remove: removeService,
  } = useFieldArray({ control: chargesForm.control, name: "services" });

  const {
    fields: costServiceFields,
    append: appendCostService,
    remove: removeCostService,
  } = useFieldArray({ control: chargesForm.control, name: "costServices" });

  // Watched rather than read once: the totals below have to move as the rows
  // are typed into. The empty-array fallback lives inside the memos that use
  // them, so a fresh `[]` on every render cannot invalidate them.
  const servicesData = useWatch({
    control: chargesForm.control,
    name: "services",
  });
  const costServicesData = useWatch({
    control: chargesForm.control,
    name: "costServices",
  });

  /**
   * Row totals and the grand total, recomputed rather than trusted.
   *
   * A row's `total` is price × qty and the API checks that it is, so it is
   * derived here on every keystroke and again on submit — the same thing the
   * order form does, for the same reason.
   */
  const { rows: serviceRows, total: servicesTotal } = useMemo(() => {
    const rows = (servicesData ?? []).map((row) => ({
      ...row,
      total: (row.qty || 0) * (row.price || 0),
    }));
    return { rows, total: rows.reduce((sum, row) => sum + row.total, 0) };
  }, [servicesData]);

  const { rows: costRows, total: costTotal } = useMemo(() => {
    const rows = (costServicesData ?? []).map((row) => ({
      ...row,
      total: (row.qty || 0) * (row.price || 0),
    }));
    return { rows, total: rows.reduce((sum, row) => sum + row.total, 0) };
  }, [costServicesData]);

  useEffect(() => {
    dispatch(getCountries());
    // A high limit rather than paging: these are pick-lists, and a table that
    // silently offers half the catalogue is worse than one request more.
    dispatch(getServices({ search: "", page: 1, limit: 1000 }));
    dispatch(getCostServices({ search: "", page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    dispatch(fetchContainerLine({ orderId, lineId: containerId }))
      .unwrap()
      .then((data) => {
        if (cancelled) return;
        setLine(data.line);
        setOrder(data.order);
        setDraft(toDraft(data.line));
        // The charges live in their own form; seeding it is part of loading the
        // container, not a separate concern the user has to trigger.
        chargesForm.reset(toServicesDraft(data.line));
        setAttachments(data.line.attachments ?? []);
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(typeof error === "string" ? error : t("linesBlock.loadFailed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderId, containerId]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  /**
   * Replace the route wholesale — every route edit goes through one writer.
   *
   * And one gate. A status waiting to be recorded names a *step*, and a step is
   * a position in the route: move the stops around and the step that word stood
   * on is no longer the one the route reaches next. Rather than let the two
   * quietly disagree until Save — or drop the status without saying so, which
   * is worse, because the dispatcher confirmed it — the change is put to them
   * before it is applied. Answering no leaves both the route and the status
   * exactly as they were.
   *
   * Only route edits that actually invalidate the pending status ask. Editing a
   * PIN, or moving a stop the status does not depend on, applies straight away.
   */
  const setRoute = async (
    next: (stops: ContainerRouteStop[]) => ContainerRouteStop[],
  ) => {
    if (!draft) return;

    const proposed = next(draft.routeStops);

    /**
     * A pending release survives any route change, because it does not stand on
     * the route.
     *
     * `planned` names no step — it is the decision to send the box out at all —
     * so there is nothing about it for a reordered stop or a switched-off
     * terminal to invalidate. Asked of `statusAfterStep` it would come back as
     * whatever step the new route reaches first, never match, and put a
     * pointless question to a dispatcher who added a customs stop to a container
     * they had just planned.
     */
    if (statusTarget && statusTarget !== "planned") {
      const stillNext = statusAfterStep(
        { routeStops: proposed },
        completedStepCount({ routeStops: proposed }),
      );

      if (stillNext !== statusTarget) {
        const ok = await confirm({
          title: t("lineEdit.statusInvalidated.title"),
          description: t("lineEdit.statusInvalidated.description", {
            status: t(`linesBlock.statuses.${statusTarget}`),
            fallback: t(`linesBlock.statuses.${currentStatus}`),
          }),
          confirmLabel: t("lineEdit.statusInvalidated.confirm"),
          cancelLabel: t("lineEdit.statusInvalidated.cancel"),
        });
        if (!ok) return;
        setStatusTarget(null);
      }
    }

    setDraft((d) => (d ? { ...d, routeStops: proposed } : d));
  };

  const setHandling = (patch: Partial<Draft["handling"]>) =>
    setDraft((d) => (d ? { ...d, handling: { ...d.handling, ...patch } } : d));

  /**
   * One handling flag, and the detail it owns.
   *
   * Turning a flag off clears its detail in the same act, on the way *down*
   * only: hiding a field that still holds a value would keep submitting it, and
   * re-checking then starts empty — which is the honest state, because nobody
   * has re-entered a temperature range yet. The server clears it too, so a
   * stored detail can never outlive its flag whichever way the write arrived.
   */
  const handlingCheckbox = (
    key: "temperatureSensitive" | "adr" | "fragile",
    label: string,
  ) => {
    const detail = {
      temperatureSensitive: "temperatureRange",
      adr: "imoClass",
      fragile: "specifications",
    }[key] as "temperatureRange" | "imoClass" | "specifications";

    return (
      <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-secondary-600">
        <input
          type="checkbox"
          checked={!!draft?.handling?.[key]}
          onChange={(e) =>
            setHandling(
              e.target.checked
                ? { [key]: true }
                : { [key]: false, [detail]: "" },
            )
          }
          disabled={disabled}
          className="h-3.5 w-3.5 shrink-0 rounded border-secondary-300 text-primary accent-primary"
        />
        {label}
      </label>
    );
  };

  const patchStop = (index: number, patch: Partial<ContainerRouteStop>) =>
    setRoute((stops) =>
      stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)),
    );

  /**
   * A new stop lands directly after the one it was added from.
   *
   * "After" rather than "at the end", because that is what adding a stop
   * actually means in the cases this exists for: a customs office *between* the
   * terminal and the client, a weighbridge *before* the return. Appending would
   * make every addition a two-step operation — add, then move it four places —
   * and the position is the part of the stop the dispatcher already knows.
   */
  const addStopAfter = (index: number) =>
    setRoute((stops) => {
      if (stops.length >= MAX_ROUTE_STOPS) return stops;
      const next = [...stops];
      next.splice(index + 1, 0, makeCustomStop());
      return next;
    });

  const removeStop = (index: number) =>
    setRoute((stops) => stops.filter((_, i) => i !== index));

  const setPending = (category: AttachmentCategory, files: File[]) =>
    (category === "customs" ? setPendingCustoms : setPendingGeneral)(files);

  /**
   * Fetch one document as a blob, to save or to open in a tab.
   *
   * Through the API rather than by linking `fileUrl` directly: the download
   * route is authenticated and scoped to this company, and a bare static URL
   * would be neither.
   */
  const downloadAttachment = async (
    attachment: ContainerLineAttachment,
    previewOnly = false,
  ) => {
    try {
      setBusyAttachment(attachment._id);
      const res = await containerOrderAPI.downloadLineAttachment(
        orderId,
        containerId,
        attachment._id,
      );
      deliverAttachment(res, attachment, previewOnly);
    } catch {
      toast.error(t("attachments.downloadFailed"));
    } finally {
      setBusyAttachment(null);
    }
  };

  /**
   * Remove one document, immediately — it is not part of the form's save.
   *
   * Asked about first, because it is the one control on this page that takes
   * effect before Save and cannot be undone by abandoning the edit: the file is
   * gone from disk by the time the button releases. Everything else here is a
   * draft until Save; this is not, so it asks.
   *
   * The API decides whether it may go: a driver's upload is the driver's, and
   * this page does not offer the button for one. A 403 here would mean the
   * button was shown when it should not have been, so the message is the
   * server's rather than a guess.
   */
  const deleteAttachment = async (attachment: ContainerLineAttachment) => {
    const agreed = await confirm({
      title: t("confirm.titles.deleteAttachment"),
      description: t("lineAttachments.confirmDelete", {
        name: attachment.originalName,
      }),
      confirmLabel: t("confirm.actions.deleteAttachment"),
      cancelLabel: t("confirm.actions.keep"),
      tone: "danger",
    });
    if (!agreed) return;

    try {
      setBusyAttachment(attachment._id);
      const res = await containerOrderAPI.deleteLineAttachment(
        orderId,
        containerId,
        attachment._id,
      );
      setAttachments((prev) => prev.filter((a) => a._id !== attachment._id));

      /**
       * Take the version token the delete just minted.
       *
       * Removing a document writes to the container, which moves its
       * `updatedAt` — and this page is still holding the one it loaded with. A
       * Save straight afterwards would be refused as a lost update and blame an
       * imaginary colleague for a change the user had just made themselves.
       * Adopting the returned token keeps the page current at the cost of
       * nothing: no refetch, and no draft disturbed, since only the token
       * moves.
       *
       * A real conflict is untouched by this — a driver's upload or another
       * desk's save moves the token somewhere this response never carried.
       */
      const updatedAt = res.data?.data?.updatedAt;
      if (updatedAt) {
        setLine((prev) => (prev ? { ...prev, updatedAt } : prev));
      }

      toast.success(t("attachments.deleted"));
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || t("attachments.deleteFailed"));
    } finally {
      setBusyAttachment(null);
    }
  };

  // A completed container is a record of what happened at the terminal, not a
  // plan: locked here and refused by the server, so the two cannot disagree.
  const completed = line?.status === "completed";
  const cancelled =
    line?.status === "cancelled" || order?.status === "cancelled";
  const locked = completed || cancelled;

  /**
   * Reading and editing are two modes, not one form with disabled inputs.
   *
   * Opening a container lands in read mode — the same rule the order page
   * follows, for the same reason: most visits are to *check* something (which
   * box, whose truck, when it is due at the terminal), and a page of live inputs
   * over a record a driver is working from invites an accidental edit. "Edit"
   * opens the form; saving returns here, which also confirms the save landed.
   *
   * A locked container has no edit mode to offer at all.
   */
  const [viewMode, setViewMode] = useState<"read" | "edit">("read");
  const reading = locked || viewMode === "read";
  const disabled = reading || saving;

  /**
   * Whether the form is holding anything the server has not been told about.
   *
   * Compared against the container as stored, re-derived through the same
   * `toDraft` the form was seeded with, so the comparison is like for like and
   * no field can be forgotten: every box on this page — the route, the toggles
   * and their reasons, the cargo rows, the box's own details, the sailing, the
   * notes — is in the draft by construction, and anything added to the draft
   * later is covered without this having to be revisited. The two things that
   * live outside it are counted separately because they are not text being
   * edited: documents waiting to be uploaded, and a step waiting to be recorded.
   */
  const dirty = useMemo(() => {
    if (!line || !draft) return false;
    if (pendingGeneral.length || pendingCustoms.length) return true;
    if (statusTarget) return true;
    // The charges are held in their own form, so they are compared in their own
    // right — against the stored rows, through the same seeding function, for
    // the same like-for-like reason the draft is.
    if (
      JSON.stringify({ services: serviceRows, costServices: costRows }) !==
      JSON.stringify(toServicesDraft(line))
    ) {
      return true;
    }
    return JSON.stringify(draft) !== JSON.stringify(toDraft(line));
  }, [
    line,
    draft,
    pendingGeneral,
    pendingCustoms,
    statusTarget,
    serviceRows,
    costRows,
  ]);

  /**
   * ── Leaving the page with work in hand ────────────────────────────────────
   *
   * The header used to carry a third button beside Edit and Save — "Done
   * editing" with nothing pending, "Discard changes" with something — and it is
   * gone: it was this module's odd one out, since the order page has only ever
   * offered Edit and Save, and two pages in one module answering "how do I stop
   * editing?" differently is a question nobody should have had to ask. Saving is
   * how you leave edit mode; navigating away is how you abandon it, and the
   * draft lives in this component so it goes with the component.
   *
   * Which leaves one way to lose work by accident rather than by choice: closing
   * the tab or reloading. That is what this covers, and it is the reason `dirty`
   * still earns its place — the browser's own prompt, raised only while there is
   * genuinely something unsaved, and never while merely reading.
   *
   * Deliberately not a router guard. In-app navigation is the deliberate act of
   * a user who has decided; the browser prompt is for the ones who have not.
   */
  useEffect(() => {
    if (reading || !dirty) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Assigning `returnValue` is what still triggers the prompt in browsers
      // that ignore `preventDefault` alone. The text is the browser's own —
      // none of them has shown a custom message for years.
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [reading, dirty]);

  /**
   * The consignment note beside the page, and whether it is open.
   *
   * Same rail the shipment form carries, for the same reason: the note is what
   * the office is transcribing the container *into*, and checking it against
   * the form means seeing both at once.
   *
   * Closed on every mount, at every width. The page is opened to read or edit
   * the container; the note is the reference someone reaches for, not the thing
   * they came for, and a rail that opens itself takes width away from the form
   * before anyone has asked for it. It used to expand itself above 1800px and
   * re-decide on every resize, which also meant it reversed a choice the reader
   * had just made by dragging the window.
   */
  const [cmrCollapsed, setCmrCollapsed] = useState(true);
  const [downloadingCmr, setDownloadingCmr] = useState(false);

  /**
   * The journey rail, on the same terms as the note and independent of it.
   *
   * Two references either side of one form, each opened when it is wanted:
   * closing one does not open the other, and neither is open on arrival. Below
   * `xl` the rail is not rendered at all — the timeline lives behind the status
   * pill in the header there — so this only governs the wide layout.
   */
  const [timelineCollapsed, setTimelineCollapsed] = useState(true);

  // A container can be cancelled from elsewhere while this page is open — its
  // order cancelled in another tab, say. Drop back rather than leaving live
  // inputs over a closed record.
  useEffect(() => {
    if (locked) setViewMode("read");
  }, [locked]);

  /**
   * Leaving the form disarms a status that was chosen but never saved.
   *
   * Fails closed, unlike the rest of the draft, which survives the trip: the
   * others are values waiting to be written, this one is an act waiting to be
   * taken. Coming back to a form quietly holding a confirmed step is how a
   * timestamp gets recorded by an unrelated save.
   */
  useEffect(() => {
    if (reading) setStatusTarget(null);
  }, [reading]);


  const numberCheck = checkContainerNumber(draft?.containerNumber ?? "");
  // What is wrong with it, in the words the driver's screen uses for the same
  // number — one mapping, two pages.
  const numberProblem = containerNumberMessage(numberCheck);

  /**
   * The container's requirement fields, read off the draft as it is typed.
   *
   * Assembled by hand because the draft holds strings — a half-typed weight has
   * to stay half-typed — and annotated so every field the gates read has to be
   * listed. The tare was missing from this object once, which made every
   * container permanently short of a tare however plainly one was typed in the
   * box above; the annotation is what stops the next omission being silent.
   *
   * `cargoWeightKg` is on it for the same reason it is on the server's reader:
   * "state what is inside" is answered by a packing list *or* by a weighed
   * figure, and a form that only passed the rows reported a container blocked
   * on a packing list it had already answered with a bridge ticket.
   */
  const startFields = useMemo<
    Record<LineStartField, unknown> & { cargoWeightKg: unknown }
  >(
    () => ({
      vehicleId: draft?.vehicleId,
      containerNumber: draft?.containerNumber,
      tareKg: numOrNull(draft?.tareKg ?? ""),
      maxPayloadKg: numOrNull(draft?.maxPayloadKg ?? ""),
      // The packing list, which stands where the VGM used to. The VGM is no
      // longer a requirement because it is no longer a field — it is the sum of
      // the tare above and these rows — so what has to be checked is the rows.
      cargoItems: draft?.cargo,
      cargoWeightKg: numOrNull(draft?.cargoWeightKg ?? ""),
    }),
    [draft],
  );

  /**
   * Everything the run still owes, whenever each part of it comes due.
   *
   * This is the *describing* list, not the gating one, and the distinction is
   * the whole of what this form gets wrong when they are confused. A dispatcher
   * fills the container in at one sitting and does not care which stop each
   * field comes due at, so every box that will eventually be needed is marked
   * required. What may be *done* right now is a different question, asked below.
   */
  const outstanding = useMemo(
    () => (draft ? missingLineStartRequirements(startFields) : []),
    [draft, startFields],
  );

  /** True for a field the run will need before it can leave its first stop. */
  const needs = (key: LineStartRequirement) => outstanding.includes(key);

  /** True once this container's run has settled the field. */
  const frozen = (key: LineLockableField) =>
    disabled || (!!line && isFieldFrozen(line, key));

  /**
   * Whether this container's run is under way.
   *
   * Not the same question as `frozen` any more: the driver and the truck stay
   * editable throughout — a run is exactly when a handover happens — but a
   * started container may not be left with nobody driving it, so what a start
   * takes away is the *clear*, not the field.
   */
  const started = !!line && hasStarted(line);

  /**
   * The notice appears when something is actually frozen, which is a step later
   * than the run starting.
   *
   * It was shown on `hasStarted` — the driver arriving at the first stop —
   * while `frozen()` above closes nothing until they leave it. So a container
   * whose driver had just reported in was told its number was fixed for the
   * rest of the run, above a container-number box that was still editable and
   * still marked required. Both statements were on screen together and only one
   * of them was true.
   */
  const showFreezeNotice = !!line && hasLeftFirstStop(line) && !locked;

  /* ── The status, as a field the office can move ──────────────────────────
   *
   * The driver's app is the normal way a container advances, and this is the
   * way it advances when there is no driver to do it: no signal at the
   * terminal, a phone left in the cab, a box the terminal's own shunter moved.
   *
   * Three rules, all of them the server's — this only declines to offer what
   * the server would refuse, so the control never leads anywhere a save
   * cannot follow:
   *
   *   · forward only, one step per save. The dropdown offers exactly two
   *     values, where the container is and where it may go next, so there is
   *     no jump to make and nothing to walk backwards to.
   *   · anchored to the step log, not to the stored status. Where the two
   *     disagree the stamps are what happened, and the server judges the same
   *     way — offering an option off the stored status would offer one the
   *     save then refuses.
   *   · the first step still owes the paperwork. The same six fields the
   *     driver is gated on, read off the draft, so filling them in and
   *     starting the container is one save rather than two.
   */
  /**
   * The status question is asked of the route *being saved*, not the stored one.
   *
   * A status is a request to record the step underneath it, and which step that
   * is depends on the route — so a dispatcher who reorders two stops and then
   * opens the status dropdown must be offered the step the reordered route
   * implies, not the one the stored route did. The server resolves it the same
   * way and refuses anything else; offering the stored answer would put a value
   * in the list that the save then rejects.
   *
   * The stamps ride along in the draft (`toStopDraft` keeps them), so the
   * completed prefix is the real one and only the shape ahead of the driver
   * differs.
   */
  const routeForStatus = useMemo(
    () =>
      draft
        ? {
            routeStops: draft.routeStops,
            status: line?.status,
            // Not the form's to change, and not derivable from the route —
            // whether the office has released this container is stored, so it
            // has to travel with the draft or the status question is asked of a
            // container that looks unreleased whatever it actually is.
            plannedAt: line?.plannedAt,
          }
        : line,
    [draft, line],
  );

  /**
   * The container as the timeline should show it: stored, with the edit on top.
   *
   * The timeline is the answer to "where is this box and what happens next", and
   * while someone is editing the route that answer is changing under their hands
   * — a stop moved, a terminal turned off, a customs call added. Drawing it from
   * the saved container meant the rail beside the form described a journey the
   * form no longer proposed, and the discrepancy was silent.
   *
   * Only the route and the cut-off are overlaid. The stamps, the events and the
   * creation date come from the stored container and are not the form's to
   * change; the draft carries the stamps forward untouched, so the completed
   * part of the timeline reads identically either way and only the plan ahead of
   * the driver moves.
   */
  const timelineLine = useMemo<ContainerOrderLine | null>(() => {
    if (!line) return null;
    if (reading || !draft) return line;
    return {
      ...line,
      routeStops: draft.routeStops,
      cutOffDate: draft.cutOffDate || null,
      cutOffTime: draft.cutOffTime,
    };
  }, [line, draft, reading]);

  const stepsRecorded = completedStepCount(routeForStatus);
  const currentStatus = routeForStatus
    ? derivedLineStatus(routeForStatus)
    : "planned";
  const nextStatus = nextLineStatus(
    routeForStatus as Parameters<typeof nextLineStatus>[0],
  );

  /**
   * Whether the one move on offer is the release rather than a step.
   *
   * The two look the same in the dropdown and are not the same act. A step is a
   * claim that something happened on the road, stamped with this moment; the
   * release is a decision that this box is going out, which stamps nothing on
   * the route. They get different confirmations, a different "i", and — below —
   * different gates.
   */
  const planning = !isLinePlanned(routeForStatus);

  /**
   * What is blocking this container *right now* — the gate it is standing at.
   *
   * The same call the driver's screen makes, against the same two tiers, so the
   * office and the cab agree about what a container may do next. They did not:
   * this form asked `missingLineStartRequirements`, the union of both gates, and
   * asked it at step 0 — so the status field was shut until the container
   * number, the tare, the max payload and the packing list were all filled in,
   * while a driver holding the same container could press "I'm here" with
   * nothing but a truck assigned. The office was stricter than its own API,
   * which checks the arrival gate and nothing else at that step.
   *
   * The tiers, and the reason there are two:
   *
   *   before step 0  the truck, and only the truck. It is what puts the
   *                  container on a screen at all, and it is dispatch's own.
   *   before step 1  the box's own figures — number, tare, max payload, what is
   *                  going inside. Not knowable at planning time: the driver is
   *                  being sent to collect a box nobody has seen, and the number
   *                  is painted on a door they have not reached yet.
   *   afterwards     nothing. A run under way is allowed to finish whatever is
   *                  changed behind the driver's back, exactly as the server
   *                  allows it.
   */
  const blocking = useMemo(
    () => (draft ? blockingRequirements(startFields, stepsRecorded) : []),
    [draft, startFields, stepsRecorded],
  );

  /**
   * The gate stands in front of the next *step*, never in front of the release.
   *
   * Planning a container says the office intends to send this box out, and that
   * decision is usually taken before anyone knows the container number or has
   * weighed anything — holding it back until the paperwork is complete would
   * make dispatch wait on figures that arrive at the terminal. What the
   * requirements protect is the driver, who cannot be sent to a gate without
   * them. The server draws the line in the same place.
   */
  const startBlocked = !planning && blocking.length > 0;

  /**
   * Which of the two gates is refusing, for the sentence that explains it.
   *
   * The arrival gate is dispatch's to clear and the departure gate is mostly
   * the driver's, so "this cannot be started" is the wrong sentence at the
   * second one — the container *has* started, and what is owed is what it needs
   * to leave the stop it is sitting at.
   */
  const blockedAtDeparture = startBlocked && stepsRecorded > 0;


  /** What the status field shows: the pending request, or where it stands. */
  const statusValue = statusTarget ?? currentStatus;

  /**
   * Closed until the container could actually start.
   *
   * Not merely "the next option is closed" — the whole control is, because
   * with nothing selectable an open dropdown is a promise it cannot keep. The
   * "i" beside the label is then the only thing worth interacting with, and it
   * says which fields are still owed.
   */
  const statusFieldDisabled = disabled || startBlocked || !nextStatus;

  /**
   * What the "i" says: why the field is shut, or what moving it will do.
   *
   * Three sentences for three situations, and the blocked one names the fields
   * by the same words the banner above uses — a tooltip that says "fill in what
   * is missing" without saying what is missing has explained nothing.
   */
  const statusTip = startBlocked
    ? t(
        blockedAtDeparture
          ? "lineEdit.statusTip.blockedDeparture"
          : "lineEdit.statusTip.blocked",
        {
          fields: blocking
            .map((key) => t(`driver.requirements.${key}`))
            .join(", "),
        },
      )
    : planning && nextStatus
      ? t("lineEdit.statusTip.plan", {
          status: t(`linesBlock.statuses.${nextStatus}`),
        })
      : nextStatus
        ? t("lineEdit.statusTip.ready", {
            status: t(`linesBlock.statuses.${nextStatus}`),
          })
        : t("lineEdit.statusTip.final");

  /**
   * Where the container is, and the one place it may go.
   *
   * Two entries rather than the whole vocabulary greyed out except two. That
   * was serviceable while the run was a fixed six steps and the status list was
   * the route; it is not now — a route can visit three added stops, so
   * `at_stop` would appear once in a list of nine and mean whichever of the
   * three the container happens to be approaching. Listing only what can
   * actually be chosen says the same thing without the ambiguity, and the "i"
   * beside the label says what choosing it will do.
   */
  /**
   * The status, named with the stop it belongs to where the word alone repeats.
   *
   * Every added stop produces the same two words — "At a stop" and "Stop done" —
   * so on a route with a customs call and a weighbridge the dropdown would offer
   * "At a stop" without saying which, and the timeline would read the same. The
   * word is the status; the stop is what makes it mean something.
   *
   * Only the added stops need it. An anchor's status already names its stop
   * ("At loading site"), and appending the company would be the same sentence
   * twice.
   */
  const labelForStatus = (
    status: ContainerLineStatus,
    stepIndex: number | null,
  ) => {
    const word = t(`linesBlock.statuses.${status}`);
    if (stepIndex === null) return word;

    const step = lineSteps(routeForStatus)[stepIndex];
    if (!step || step.role !== "custom") return word;

    const stop = draft?.routeStops[step.stopIndex];
    const name = stop ? stopTitle(stop, t) : "";
    return name ? `${word} — ${name}` : word;
  };

  const statusOptions = [
    {
      value: currentStatus,
      // The step that produced where it stands is the one before the next.
      label: labelForStatus(currentStatus, stepsRecorded > 0 ? stepsRecorded - 1 : null),
    },
    ...(nextStatus && !startBlocked
      ? [{ value: nextStatus, label: labelForStatus(nextStatus, stepsRecorded) }]
      : []),
  ];

  /**
   * Ask before taking the step, then hold it until Save.
   *
   * A confirmation rather than a silent change because of what it writes: a
   * timestamp on the container's timeline, with this moment as the time and
   * nobody's location — a record that reads, forever, as the moment this
   * happened. The dialog says so in those terms, and names the step.
   */
  const handleStatusSelect = async (target: string) => {
    if (!line || target === statusValue) return;

    // Back to where it stands — nothing to confirm, and nothing sent.
    if (target === currentStatus) {
      setStatusTarget(null);
      return;
    }

    // The dropdown disables everything else; this is the backstop for a
    // keyboard or a stale render, and it fails closed.
    if (target !== nextStatus || startBlocked) return;

    /**
     * The release is asked about in its own words.
     *
     * Reusing the step dialog here would have promised the wrong thing twice
     * over: it says a timestamp is going onto the timeline at this moment, and
     * planning stamps nothing on the route; and it warns that the step cannot
     * be undone, where a container planned by mistake is simply cancelled.
     * What a dispatcher needs told is the one consequence that is real — the
     * driver can see this box now.
     */
    const ok = await confirm(
      planning
        ? {
            title: t("lineEdit.statusPlan.title"),
            description: t("lineEdit.statusPlan.description"),
            confirmLabel: t("lineEdit.statusPlan.confirm"),
            cancelLabel: t("lineEdit.cancel"),
          }
        : {
            // Named with its stop, so a route with two added stops does not ask
            // "record At a stop?" twice and mean different things.
            title: t("lineEdit.statusAdvance.title", {
              status: labelForStatus(
                target as ContainerLineStatus,
                stepsRecorded,
              ),
            }),
            description: t("lineEdit.statusAdvance.description"),
            confirmLabel: t("lineEdit.statusAdvance.confirm"),
            cancelLabel: t("lineEdit.cancel"),
          },
    );
    if (!ok) return;

    setStatusTarget(target as ContainerLineStatus);
  };

  const tare = numOrNull(draft?.tareKg ?? "");
  const payload = numOrNull(draft?.maxPayloadKg ?? "");

  /**
   * What the packing list weighs. Derived from the cargo rows and shown
   * read-only, never typed.
   *
   * It used to be an input that wrote the VGM (`VGM = tare + what you type`),
   * which made it a second way to set the same figure — and one that quietly
   * disagreed with the packing list underneath it the moment a row was added.
   * A container has exactly one statement of what is inside, and this is it:
   * the rows.
   */
  const listedCargoKg = useMemo(
    () => cargoTotalWeight(draft?.cargo ?? []),
    [draft?.cargo],
  );

  /**
   * The declared load, as the box currently holds it.
   *
   * Empty means "not weighed" rather than "weighs nothing" — the fallback below
   * is what that empties into — while a typed `0` is a real declaration about
   * an empty move. `numOrNull` draws exactly that line.
   */
  const declaredCargoKg = numOrNull(draft?.cargoWeightKg ?? "");

  /**
   * What is inside, as the form will store it.
   *
   * The declaration when there is one, the rows when there is not. Everything
   * downstream on this page — the VGM, the weight rules, the totals bar — reads
   * this rather than the sum, so the page and the server cannot end up
   * describing the same container with two different loads.
   */
  const cargoKg = effectiveCargoKg(declaredCargoKg, listedCargoKg);

  /**
   * The VGM this container would be saved with, from the two fields it is made
   * of.
   *
   * Read off the *draft* rather than off the stored container, so the figure
   * moves as the tare is corrected and as packing-list rows are added — which
   * is the whole reason to show a derived value on a form at all. The server
   * computes the identical sum on save (`deriveVgmKg`) and is the only writer;
   * nothing here is posted.
   *
   * Null without a tare, exactly as the server has it: a container whose plate
   * has not been read has no gross mass to declare, and showing the bare cargo
   * weight in its place would be a figure that looks like a VGM and is short by
   * the several tonnes the empty box weighs.
   */
  const derivedVgm = deriveVgm({
    tareKg: tare,
    declaredCargoKg,
    listedKg: listedCargoKg,
  });

  // What the weight rules are checked against. The same value that will be
  // stored, so the form cannot accept a set the save then refuses.
  const vgm = derivedVgm;

  /**
   * How the four weights stand, recomputed on every keystroke.
   *
   * Errors are arithmetic contradictions and block the save; warnings are
   * physical overloads and are recorded loudly. Same function the driver's
   * screen uses, and the mirror of the one the API enforces.
   */
  const weights = useMemo(
    () =>
      evaluateWeights({
        tareKg: tare,
        maxPayloadKg: payload,
        vgmKg: vgm,
        // Both figures: the rules compare them, and decide between them.
        cargoKg: listedCargoKg,
        declaredCargoKg,
      }),
    [tare, payload, vgm, listedCargoKg, declaredCargoKg],
  );

  const allowance = weights.allowanceKg;
  const vgmIssue = issueFor(weights, "vgmKg");
  // The two plate figures the wrong way round. Reported on the payload, which
  // is the one of the pair a dispatcher is most likely to have mistyped.
  const plateIssue = issueFor(weights, "maxPayloadKg");
  // Whichever box the load problem landed on — the declaration when one is in
  // force, the table when it is not.
  const cargoIssue = cargoIssueOf(weights);

  /** `lineWeights.<code>` with its numbers filled in. */
  const issueText = (issue?: { code: string; values: Record<string, string> }) =>
    issue ? t(`lineWeights.${issue.code}`, issue.values) : undefined;

  /**
   * The route-order rule — pickup ≤ on-site ≤ delivery ≤ cut-off — as it stands
   * in the draft, keyed by the field each violation belongs under.
   *
   * The same rule the order is held to, over the same four moments, from the
   * same implementation (`utils/routeSequence`). A container inherits its slots
   * from its order and may then move them; without this, the schedule the order
   * refuses would simply be reachable one container at a time. The server
   * refuses it too, against the update merged over what is stored.
   */
  /**
   * What each stop is still missing, recomputed as the form is typed into.
   *
   * The order's three requirements, now the container's: a company picked from
   * the directory, a street, a city, and the map location of that street. The
   * API holds the same rule (`lineStopValidator`), so this is the form saying it
   * before the save rather than after.
   */
  const stopIssues = useMemo(
    () => (draft ? findAllStopIssues(draft.routeStops) : null),
    [draft],
  );

  /**
   * The route-order rule — every stop in turn, then the cut-off — as it stands
   * in the draft, keyed by the field each violation belongs under.
   *
   * The same rule the order is held to, from the same implementation
   * (`utils/routeSequence`), now over however many stops this container has
   * rather than a fixed four. A container inherits its slots from its order and
   * may then move them and add to them; without this, the schedule the order
   * refuses would simply be reachable one container at a time. The server
   * refuses it too, against the update merged over what is stored.
   *
   * Stops that are not being made drop out. There is no truck going to them, so
   * there is no moment to be out of order, and a date left behind on a stop that
   * was turned off must not be able to refuse the save.
   */
  const slotErrors = useMemo(() => {
    if (!draft) return {} as Record<string, string>;

    // Kept alongside the slot so a violation can be attributed back to the stop
    // it belongs to: the rule reports which *slot* is out of order, and with a
    // variable route there is no fixed name to map that back from.
    const active = draft.routeStops
      .map((stop, index) => ({ stop, index }))
      .filter(({ stop }) => stop.enabled !== false);

    /**
     * A name per slot, and the stop it belongs to.
     *
     * The three anchors keep the names the order form uses, so the six
     * purpose-written sentences ("the cut-off is before the on-site slot") still
     * apply to the route they were written about. Added stops have no such name
     * — there can be any number of them — so they take a positional one and are
     * described in the generic sentence by their label instead.
     *
     * Both kinds are unique across a route (one anchor per role, one index per
     * position), which is what lets a violation be attributed back to the card
     * that has to change.
     */
    const ownerOf = new Map<string, number>();
    const cutOff = {
      name: "cutOff",
      label: t("route.cutOffDate"),
      day: dayOf(draft.cutOffDate),
      clock: timeOf(draft.cutOffTime),
    };

    const slots: Array<{
      name: string;
      label?: string;
      day: string | null;
      clock: string | null;
    }> = [];
    let cutOffPlaced = false;

    active.forEach(({ stop, index }) => {
      const name = ANCHOR_SLOT_NAMES[stop.role] ?? `stop-${index}`;
      ownerOf.set(name, index);
      slots.push({
        name,
        label: stopTitle(stop, t),
        day: dayOf(stop.date),
        clock: timeOf(stop.time),
      });

      /**
       * The cut-off closes the drop-off, not the whole route.
       *
       * It follows the drop-off terminal immediately and *before* anything
       * scheduled after it — an empty-depot return, a mandated rest — because
       * those genuinely happen once the deadline has been met. Putting it last,
       * which is where it sat while a route was four fixed points, would make
       * every such stop read as breaching a cut-off it has nothing to do with.
       */
      if (stop.role === "terminalDropoff") {
        slots.push(cutOff);
        cutOffPlaced = true;
      }
    });

    // No drop-off on the route — the box is staying where it is. The cut-off is
    // still a deadline the run answers to, so it closes the route instead.
    if (!cutOffPlaced) slots.push(cutOff);

    const violations = findRouteSequenceViolations(slots);

    const errors: Record<string, string> = {};
    for (const violation of violations) {
      const message = translateValidation(
        violation.messageKey,
        violation.values,
      );

      if (violation.slot.name === "cutOff") {
        errors[violation.part === "date" ? "cutOffDate" : "cutOffTime"] = message;
        continue;
      }

      const index = ownerOf.get(violation.slot.name);
      // Keyed by position rather than by stop name, because that is how the
      // cards below are keyed and a route can hold two stops with the same one.
      if (index !== undefined) errors[`${index}.${violation.part}`] = message;
    }
    return errors;
  }, [draft, t]);

  /**
   * The container the note is drawn from: what is stored, or what is being
   * typed.
   *
   * In read mode that is the saved container. In edit mode the draft is
   * overlaid on top of it, so the note fills in as the form is filled in —
   * the same live preview the shipment form gives, and the reason the note
   * sits beside the form rather than behind a button. Only the fields the CMR
   * actually prints are overlaid; the rest of the container is untouched.
   */
  const previewLine = useMemo<ContainerOrderLine | null>(() => {
    if (!line) return null;
    if (reading || !draft) return line;
    return {
      ...line,
      containerNumber: draft.containerNumber,
      notes: draft.notes,
      cargoItems: fromCargoRows(draft.cargo),
      routeStops: draft.routeStops,
      // The note picks its two ends off the route — and off the anchors when the
      // route does not reach — so the anchor mirrors are refreshed here too.
      // The server does this on save; the preview has to do it as the form is
      // typed into, or a terminal being retyped would still print the old one.
      ...Object.fromEntries(
        draft.routeStops
          .filter((stop) => stop.role !== "custom")
          .map((stop) => [stop.role, stop]),
      ),
      vehicleId: draft.vehicleId
        ? { _id: draft.vehicleId, registrationNumber: draft.vehiclePlate }
        : null,
    };
  }, [line, draft, reading]);

  const cmrOrder = useMemo(
    () =>
      previewLine
        ? buildContainerCmrOrder(previewLine, order ?? undefined)
        : null,
    [previewLine, order],
  );

  /**
   * The note as a file. Drawn from the same object the preview shows, so what
   * downloads is what is on screen — including unsaved edits, which is what a
   * dispatcher printing a note for a driver waiting at the gate expects.
   */
  const handleDownloadCmr = async () => {
    if (!previewLine || downloadingCmr) return;
    try {
      setDownloadingCmr(true);
      await downloadContainerCmrPdf(previewLine, order ?? undefined, countries);
    } catch {
      toast.error(t("lineCmr.downloadFailed"));
    } finally {
      setDownloadingCmr(false);
    }
  };

  /**
   * ── Charges on a container that has already finished ──────────────────────
   *
   * These two go straight to the API rather than through the form, because by
   * this point there is no form: a completed container is a record and Save is
   * not offered. Invoicing is what happens next, though, and demurrage, waiting
   * time and an extra lift are all discovered once the box is back — so the
   * charges stay reachable when nothing else does.
   *
   * The response is the whole container, so the page adopts it wholesale: the
   * row list, and with it the version token, come back current. That matters
   * more here than elsewhere, since these writes bypass the form entirely and a
   * stale token would refuse the next thing anyone did.
   */
  const applySavedLine = useCallback(
    (saved: ContainerOrderLine) => {
      setLine(saved);
      chargesForm.reset(toServicesDraft(saved));
      setAttachments(saved.attachments ?? []);
    },
    // `chargesForm` is stable for the component's lifetime; RHF guarantees it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** The server's own sentence, or a generic one when it did not give a reason. */
  const apiMessage = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message || fallback;

  /**
   * Built per list rather than written out four times.
   *
   * The billable and the internal list differ in exactly one thing — which
   * endpoint they post to — and that difference is already a path segment. Four
   * hand-written handlers would be four places for the response handling, the
   * error message or the re-seed to drift apart between two lists that must
   * behave identically.
   */
  const chargeHandlers = (kind: "services" | "cost-services") => ({
    add: async (service: Record<string, unknown>) => {
      try {
        const res = await containerOrderAPI.addLineService(
          orderId,
          containerId,
          kind,
          service,
        );
        applySavedLine(res.data.data.line);
        toast.success(t("lineServices.added"));
      } catch (error) {
        toast.error(apiMessage(error, t("lineServices.addFailed")));
      }
    },
    remove: async (serviceId: string) => {
      try {
        const res = await containerOrderAPI.deleteLineService(
          orderId,
          containerId,
          kind,
          serviceId,
        );
        applySavedLine(res.data.data.line);
        toast.success(t("lineServices.removed"));
      } catch (error) {
        toast.error(apiMessage(error, t("lineServices.removeFailed")));
      }
    },
  });

  const billable = chargeHandlers("services");
  const internal = chargeHandlers("cost-services");

  const addServiceAfterCompletion = billable.add;
  const removeServiceAfterCompletion = billable.remove;
  const addCostAfterCompletion = internal.add;
  const removeCostAfterCompletion = internal.remove;

  const submit = async () => {
    if (!draft || !line || saving || reading) return;

    // Cargo is the one block on this form that refuses to save half-filled: a
    // row that names goods has to say how many, of what, and what they are.
    if (Object.keys(findCargoRowErrors(draft.cargo)).length) {
      setShowCargoErrors(true);
      toast.error(t("lineCargo.fixBeforeSaving"));
      return;
    }

    /**
     * The box's number has to be a container number.
     *
     * Refused rather than warned about: the number is what every piece of
     * paperwork downstream is filed against, and a wrong one is not a detail
     * to be corrected later — it points at a different box, or at no box.
     *
     * Skipped once the field is frozen, and that exception is the whole reason
     * this is safe. A container that started before this rule existed may be
     * carrying a number that fails it, and its number can no longer be edited;
     * blocking there would mean nothing else on the container — a corrected
     * weight, a note, a charge — could ever be saved again.
     */
    if (numberProblem && !frozen("containerNumber")) {
      toast.error(t(numberProblem.key, numberProblem.values));
      return;
    }

    // The four weights against each other — the model refuses these too,
    // because the driver can reach the same state from their own screen. Already
    // marked on the fields themselves, so this is the backstop rather than the
    // explanation.
    if (weights.errors.length) {
      toast.error(issueText(weights.errors[0])!);
      return;
    }

    /**
     * A stop is where a truck is sent, so it has to be a place.
     *
     * The order's own rule, now enforced on the container that inherits and then
     * diverges from it: each stop names a directory record, has a street and a
     * city, and carries the map location that only a pick from the suggestions
     * can produce. Marked on the cards themselves, so this is the backstop.
     */
    if (stopIssues) {
      const firstIssue = firstStopIssue(draft.routeStops, stopIssues);
      if (firstIssue) {
        setShowStopErrors(true);
        toast.error(translateValidation(firstIssue));
        return;
      }
    }

    // A schedule that does not run in route order is not a partial fact, it is
    // an impossible one. Said beside the offending box as well, so this is the
    // backstop rather than the explanation.
    const firstSlotError = Object.values(slotErrors)[0];
    if (firstSlotError) {
      toast.error(firstSlotError);
      return;
    }

    // Nothing to check for the driver and the truck: both are picked from a
    // directory that never lets a typed name become a value, so the label and
    // the id are set together or not at all. This used to be two guards against
    // a name typed and never picked, which the field itself now prevents.
    setSaving(true);

    const payloadData: ContainerLineFormData = {
      containerNumber: draft.containerNumber.trim(),
      containerType: (draft.containerType || "") as ContainerType | "",
      isoCode: draft.isoCode.trim(),
      tareKg: numOrNull(draft.tareKg),
      maxPayloadKg: numOrNull(draft.maxPayloadKg),
      // The declaration, and `null` when the box is empty — which genuinely
      // clears it and hands the figure back to the packing list.
      cargoWeightKg: numOrNull(draft.cargoWeightKg),
      // No VGM. It is derived server-side from the tare and the packing list,
      // and the API strips it from anything a client sends — see `deriveVgmKg`.
      sealNumber: draft.sealNumber.trim(),
      // Both go every time, empty included: "" is what clears them, and both
      // are answers a dispatcher has to be able to take back out.
      loadStatus: draft.loadStatus as ContainerLoadStatus | "",
      mrnNumber: draft.mrnNumber.trim(),
      // No driver. It is written by whoever records a step, and the API strips
      // it from anything a client sends — see `driverId` in the line model.
      vehicleId: draft.vehicleId,
      shippingLine: draft.shippingLine.trim(),
      vesselName: draft.vesselName.trim(),
      voyageNumber: draft.voyageNumber.trim(),
      blNumber: draft.blNumber.trim(),
      bookingRef: draft.bookingRef.trim(),
      cutOffDate: draft.cutOffDate || null,
      cutOffTime: draft.cutOffTime.trim(),
      notes: draft.notes.trim(),
      cargoItems: fromCargoRows(draft.cargo),
      /**
       * The whole handling block, every time.
       *
       * Sent even when every flag is off, because "off" is a statement: a
       * container that inherited ADR from its order and turns out not to be
       * carrying dangerous goods has to be able to say so. The details ride
       * along as typed and the server clears the ones whose flag is off, so a
       * temperature range cannot outlive the flag that explained it.
       */
      handling: {
        temperatureSensitive: draft.handling.temperatureSensitive,
        temperatureRange: draft.handling.temperatureRange.trim(),
        adr: draft.handling.adr,
        imoClass: draft.handling.imoClass,
        fragile: draft.handling.fragile,
        specifications: draft.handling.specifications.trim(),
      },
      // The charges, with every row's total recomputed from its own price and
      // quantity — the API checks the arithmetic and will not take a total that
      // disagrees with the two numbers beside it. Stripped of the fields the
      // server owns on the way out — see `forSave`.
      services: forSave(serviceRows),
      costServices: forSave(costRows),
      // The whole route, in order — order is meaning here, and there is no way
      // to say "this stop moved" as a patch without also saying where everything
      // else now stands. The server merges each stop over its stored self and
      // puts the driver's stamps back, and refuses any change to the part of the
      // route the container has already driven.
      routeStops: routeStopsPayload(draft.routeStops),
      // Additions only. The stored list is never sent back, so this save cannot
      // drop a document the driver filed while the form was open.
      attachments: pendingGeneral,
      customsAttachments: pendingCustoms,
      // Sent only when a step is actually being asked for, *and* only when it
      // is still the step this route reaches next. An ordinary edit carries no
      // status at all, which is what keeps a save that changes a gate PIN from
      // also being an assertion about where the container is; and the second
      // condition is the backstop for a target that outlived the route it was
      // chosen against, which `setRoute` should already have caught.
      ...(statusTarget &&
      statusTarget !== currentStatus &&
      statusTarget === nextStatus
        ? { status: statusTarget }
        : {}),
    };

    try {
      const saved = await dispatch(
        updateContainerLine({
          orderId,
          lineId: line._id,
          data: withVersion(payloadData, line),
        }),
      ).unwrap();
      toast.success(t("toasts.lineUpdated"));
      // Re-seeded from what the server stored, version token included, so a
      // second save is not rejected as stale and the page shows what was kept.
      setLine(saved);
      setDraft(toDraft(saved));
      // Re-seeded from what was stored, so a row the server rejected, renumbered
      // or stamped (an author, an invoice link) reads back as it actually is.
      chargesForm.reset(toServicesDraft(saved));
      // Consumed. The status now shown is the one the server derived from the
      // step it recorded, so any pending request is answered by definition.
      setStatusTarget(null);
      // The saved container carries the whole document list, the driver's
      // uploads included — so this is also how the page catches up with
      // anything filed from the yard while the form was open.
      setAttachments(saved.attachments ?? []);
      setPendingGeneral([]);
      setPendingCustoms([]);
      // Back to reading: it confirms the save landed and returns the page to
      // the state most visits want it in.
      setViewMode("read");
    } catch (error) {
      toast.error(typeof error === "string" ? error : t("toasts.lineFailed"));
    } finally {
      setSaving(false);
    }
  };

  const backToOrder = () => navigate(`/container-orders/${orderId}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-mist">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("linesBlock.loading")}
      </div>
    );
  }

  if (loadError || !line || !draft || !order) {
    return (
      <div className="space-y-3 py-16 text-center">
        <p className="text-sm text-signal">
          {loadError ?? t("linesBlock.loadFailed")}
        </p>
        <Button type="button" variant="outline" onClick={backToOrder}>
          {t("linePage.backToOrder")}
        </Button>
      </div>
    );
  }

  const statusStyle =
    CONTAINER_LINE_STATUS_STYLES[line.status] ?? "bg-secondary-100 text-secondary-700";

  /**
   * The cut-off, rendered inside whichever card is the drop-off terminal.
   *
   * It lives with the terminal that enforces it, not in a booking block three
   * sections up — and it is stored on the container itself, where the driver app
   * and the batch list both read it. When the drop-off is not being used it has
   * no card to sit in and moves to the foot of the route, because a cut-off is
   * still a deadline the whole run answers to even when nobody is returning the
   * box to a terminal.
   */
  const cutOffFields = (
    <>
      <FormField
        layout="inline"
        size="sm"
        label={t("route.cutOffDate")}
        error={slotErrors.cutOffDate}
      >
        <Input
          size="sm"
          type="date"
          value={draft.cutOffDate}
          onChange={(e) => set("cutOffDate", e.target.value)}
          disabled={disabled}
          error={!!slotErrors.cutOffDate}
        />
      </FormField>
      <FormField
        layout="inline"
        size="sm"
        label={t("route.cutOffTime")}
        error={slotErrors.cutOffTime}
      >
        <Input
          size="sm"
          type="time"
          value={draft.cutOffTime}
          onChange={(e) => set("cutOffTime", e.target.value)}
          disabled={disabled}
          error={!!slotErrors.cutOffTime}
        />
      </FormField>
    </>
  );

  const dropoff = draft.routeStops.find(
    (stop) => stop.role === "terminalDropoff",
  );
  const cutOffHomeless = !dropoff || dropoff.enabled === false;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      /* The shipment form's shell, for the same reason it has one: a document
         preview beside a form only works if the two scroll independently, and
         that means the page itself must not scroll at all. `h-full` takes the
         height the app's <main> already has, `overflow-hidden` refuses to grow
         past it, and the two panes below each own a scrollbar. Without this the
         page scrolled as one and the preview slid off the top of the form it
         was meant to be checked against. */
      className="flex h-full flex-col overflow-hidden"
    >
      {/* Outside the scroll area rather than stuck to the top of it — the row
          never moves, so Save and the mode switch are always where they were. */}
      <div className="shrink-0 border-b border-[rgba(11,15,14,0.08)] bg-white">
        <div className="flex w-full flex-wrap items-center gap-2 px-3 py-2.5 sm:px-4">
          <button
            type="button"
            onClick={backToOrder}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[rgba(11,15,14,0.15)] text-ink hover:bg-bone"
            aria-label={t("linePage.backToOrder")}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <span className="rounded-md bg-ink px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
            {t("linePage.badge")}
          </span>

          <span className="truncate text-[15px] font-semibold text-ink">
            {line.lineNumber}
          </span>

          {/* The status pill — and, below `xl`, the handle for the timeline
              too. The rail has no room at those widths and the whole thing used
              to be laid across the top of the page instead, which pushed the
              container's own details a screen down. The pill is the right place
              for it: the timeline is what the pill is a summary of. */}
          <ContainerLineTimelinePopover
            // The live route, so the popover and the rail never disagree.
            line={timelineLine ?? line}
            // The container's own vocabulary, not the order's — a container is
            // `planned`, never `draft`.
            statusLabel={t(`linesBlock.statuses.${line.status}`)}
            statusClassName={statusStyle}
          />

          <div className="ml-auto flex items-center gap-2">
            {/* The note's own pane carries a Download button, so this one only
                appears where that pane does not: below `lg`, where there is no
                room for a second column at all, and whenever the pane is
                collapsed. Between them the document is always one click away
                and the button is never on screen twice. */}
            <Button
              type="button"
              variant="outline"
              className={cmrCollapsed ? "" : "lg:hidden"}
              onClick={handleDownloadCmr}
              isLoading={downloadingCmr}
            >
              <Download className="mr-1.5 h-4 w-4" />
              {t("lineCmr.download")}
            </Button>
            {/* ── Into edit mode, and only that ─────────────────────────────
                Edit in read mode, Save in edit mode, and nothing else — the
                order page's header exactly. The button that used to sit here in
                edit mode ("Done editing" / "Discard changes") is gone: this was
                the only page in the module that offered a third way out, and
                the way out is Save. */}
            {!locked && reading && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setViewMode("edit")}
                disabled={saving}
              >
                <PencilLine className="mr-1.5 h-4 w-4" />
                {t("linePage.edit")}
              </Button>
            )}
            {!reading && (
              <Button type="submit" variant="primary" isLoading={saving}>
                <Save className="mr-1.5 h-4 w-4" />
                {t("lineEdit.save")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* ── The two panes ──────────────────────────────────────────────────
          Split the way the shipment form splits: the container on the left
          with the timeline railed inside it, the consignment note on the
          right, each scrolling in its own right and neither taking the page
          with it. The proportions are the shipment form's too — 55/45 open,
          90/10 collapsed — and below `lg` the split gives way to one column,
          which is where the note stops being rendered at all. */}
      <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
        <div
          className={`w-full min-w-0 overflow-y-auto overscroll-contain border-gray-300 transition-all duration-300 lg:border-r ${
            cmrCollapsed ? "lg:flex-1" : "lg:w-[55%]"
          }`}
        >
          {/* The pane's content row, and it fills the pane.

              The cap that keeps the form readable — the shipment page's own
              1440px — moved down onto the form column itself. It used to sit
              here, around the form *and* the rail together, which centred the
              pair and left the rail stranded in the middle of a wide screen:
              past about 1500px the leftover split in two, and the half of it on
              the right opened a gap between this rail and the note's, growing
              with every pixel of window. The two are the same kind of thing and
              belong against the same edge.

              The gutter is the shipment page's too, halving on a phone — 24px
              either side of a 360px screen is a sixth of it — and the tall
              bottom padding keeps the last card clear of the pane's edge. */}
          <div className="flex w-full px-3 pb-10 pt-2 sm:px-6">
            {/* The container, and beside it its journey.

                The timeline sits outside the read/edit switch entirely — one
                instance serving both modes — because it is a record rather
                than a field: none of it is this form's to change, and it
                should not move or re-render when someone flips into editing. */}
            <div className="min-w-0 flex-1">
              <div className="mx-auto w-full max-w-360 space-y-3">
        {/* No timeline card here below `xl` any more — it is behind the status
            pill in the header instead. A record most visits do not come to read
            should not be the first screenful of every one of them. */}

        {/* One banner for every read-only reason. Only the sentence changes:
            why this particular container is closed. */}
        {locked && (
          <p
            className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] ${
              completed ? "bg-[#E6F2EC] text-[#2F7A5B]" : "bg-bone text-mist"
            }`}
          >
            <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-medium">{t("lineEdit.readOnlyBadge")}</span>{" "}
              {t(completed ? "lineEdit.completedLocked" : "lineEdit.readOnly")}
            </span>
          </p>
        )}

        {/* What the container is blocked on now, in the words of the gate that
            is refusing. Drawn from `blocking` rather than `outstanding`: a
            container missing only its number is not blocked before the first
            step, and telling a dispatcher the driver cannot start sent them
            hunting for a figure painted on a door nobody has reached. The boxes
            themselves stay marked required by `needs`, which is the honest way
            to say "this is still owed" without claiming it is owed yet. */}
        {!locked && blocking.length > 0 && (
          <p className="flex items-start gap-2 rounded-lg bg-[#FCEEE2] px-3 py-2 text-[12px] text-[#8A5A28]">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {t(
                blockedAtDeparture
                  ? "lineEdit.departureBlocked"
                  : "lineEdit.startBlocked",
                {
                  fields: blocking
                    .map((key) => t(`driver.requirements.${key}`))
                    .join(", "),
                },
              )}
            </span>
          </p>
        )}

        {/* Only while editing: it explains why half the form is greyed out,
            which is not a question read mode raises. */}
        {showFreezeNotice && !reading && (
          <p className="flex items-start gap-2 rounded-lg bg-bone px-3 py-2 text-[12px] text-[#5A6460]">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t("lineEdit.startedFrozen")}
          </p>
        )}

        {/* ── Which booking this box belongs to ──────────────────────────────
            Identical in both modes, and presented rather than shown as inputs:
            none of it is this page's to change, and an empty box beside
            "Booking number" reads as something waiting to be filled in. The
            order number is the only part that acts — it goes back to the order,
            which is where all three are edited. */}
        <SectionCard icon={FileText} title={t("linePage.sections.order")}>
          <FieldGrid>
            <Field
              label={t("linePage.orderNumber")}
              mono
              value={
                <button
                  type="button"
                  onClick={backToOrder}
                  className="font-semibold text-copper underline-offset-2 hover:underline"
                >
                  {order.orderNumber}
                </button>
              }
            />
            <Field
              label={t("labels.reference")}
              value={order.referenceNumber}
              mono
            />
            <Field
              label={t("labels.bookingNumber")}
              value={order.contractNumber}
              mono
            />
            {/* ── Who deals with this container ────────────────────────────
                Inside the order card, which is the one block identical in both
                modes — and that is exactly why it belongs here. The handler is
                not part of the form: it has its own endpoint, it is saved by
                the press rather than by Save, and it stays changeable on a
                completed or cancelled container, which the form itself refuses
                outright. Putting it among the form's fields would have tied it
                to a Save button that is not there when it is most needed.

                Unlike the three fields beside it, this one acts: those are the
                container's provenance and belong to the order, this belongs to
                the container. */}
            <Field
              label={t("handler.label")}
              value={
                <span className="flex items-center gap-2">
                  <HandlerAssign
                    handler={line.handlerId ?? null}
                    label={t("handler.forContainer", {
                      container: line.lineNumber,
                    })}
                    onAssign={async (handlerId) => {
                      const res = await containerOrderAPI.setLineHandler(
                        orderId,
                        line._id,
                        handlerId,
                      );
                      /**
                       * Fold the answer back in, rather than refetching.
                       *
                       * One field changed and the server says what it is now.
                       * A full re-read would also pull back the stamps, the
                       * route and the event log — and, on a page with unsaved
                       * edits in the form below, would be a refetch whose
                       * result cannot safely be applied.
                       *
                       * The timeline entry the server just wrote is the one
                       * thing not folded in. It appears on the next read of the
                       * page, which is the same deal the charge endpoints make.
                       */
                      const handler = res.data?.data?.handler ?? null;
                      setLine((prev) =>
                        prev ? { ...prev, handlerId: handler } : prev,
                      );
                      return res;
                    }}
                    onError={(message) => toast.error(message)}
                  />
                  <span className="text-[13px]">
                    {line.handlerId
                      ? `${line.handlerId.firstName ?? ""} ${
                          line.handlerId.lastName ?? ""
                        }`.trim()
                      : t("handler.unassigned")}
                  </span>
                </span>
              }
            />
          </FieldGrid>
          <p className="mt-2 text-[11px] text-mist">{t("linePage.orderHint")}</p>
        </SectionCard>

        {/* Read mode is a different presentation, not the same form greyed out
            — see ContainerLineReadonly. The order block above is outside the
            switch: it is the same in both. */}
        {reading ? (
          <ContainerLineReadonly line={line} />
        ) : (
          <>
        {/* ── How far the run has got ────────────────────────────────────────
            Its own block, above everything the form describes, because it is
            the only control here that records something happening rather than
            correcting something written. It belongs to the whole container —
            not to the box's identity on the left, and not to who is driving it
            on the right — and it is the one field on this page that also reads
            back on the timeline beside it. */}
        <SectionCard icon={Milestone} title={t("lineEdit.sections.progress")}>
          <FormField
            layout="inline"
            size="sm"
            label={t("lineEdit.fields.status")}
            labelAdornment={
              <InfoTip label={t("lineEdit.statusTip.trigger")} text={statusTip} />
            }
          >
            <div className="max-w-xs">
              <Select
                size="sm"
                value={statusValue}
                onChange={(e) => handleStatusSelect(e.target.value)}
                options={statusOptions}
                disabled={statusFieldDisabled}
                className={statusFieldDisabled ? "bg-bone" : ""}
              />
            </div>
          </FormField>
        </SectionCard>

        {/* ── The box itself ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
          <SectionCard icon={ContainerIcon} title={t("lineEdit.sections.container")}>
            <div className="space-y-1">
              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.containerNumber")}
                required={needs("containerNumber")}
                hint={
                  numberCheck.state === "valid"
                    ? t("lineEdit.hints.checkDigitValid")
                    : t("lineEdit.hints.containerNumberFormat")
                }
                error={
                  numberProblem
                    ? t(numberProblem.key, numberProblem.values)
                    : undefined
                }
              >
                <Input
                  size="sm"
                  value={draft.containerNumber}
                  onChange={(e) =>
                    set("containerNumber", e.target.value.toUpperCase())
                  }
                  disabled={frozen("containerNumber")}
                  error={numberCheck.state === "invalid"}
                  placeholder={t("lineEdit.placeholders.containerNumber")}
                  className="font-mono uppercase tracking-wider"
                />
              </FormField>

              <FormField layout="inline" size="sm" label={t("labels.containerType")}>
                <Select
                  size="sm"
                  value={draft.containerType}
                  onChange={(e) => set("containerType", e.target.value)}
                  options={CONTAINER_TYPE_OPTIONS}
                  placeholder={t("placeholders.selectContainerType")}
                  disabled={disabled}
                />
              </FormField>

              {/* ── Loaded or empty ──────────────────────────────────────
                  Directly under the type, because the two together are what a
                  gate asks for in one breath — "40ft high cube, full" — and
                  because it belongs with the facts about the box rather than
                  with the weights, which are measurements of what is in it.

                  Deliberately not derived from the order's direction. Direction
                  says which way the box changes state across the run, never
                  which state it is in now: an outbound container leaves the
                  terminal empty and returns full, and both halves are the same
                  order. Nor from the step log — a box may be pre-loaded before
                  the truck arrives, or leave a site still empty because the
                  loading was called off. So it is stated, and the blank option
                  is a real answer meaning nobody has said yet. */}
              <FormField
                layout="inline"
                size="sm"
                label={t("labels.loadStatus")}
                hint={t("hints.loadStatus")}
              >
                <Select
                  size="sm"
                  value={draft.loadStatus}
                  onChange={(e) => set("loadStatus", e.target.value)}
                  options={CONTAINER_LOAD_STATUS_VALUES.map((value) => ({
                    value,
                    label: t(`loadStatus.${value}`),
                  }))}
                  placeholder={t("placeholders.loadStatus")}
                  disabled={disabled}
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("labels.isoCode")}
                hint={t("hints.isoCode")}
              >
                <Input
                  size="sm"
                  value={draft.isoCode}
                  onChange={(e) => set("isoCode", e.target.value.toUpperCase())}
                  disabled={disabled}
                  placeholder={t("placeholders.isoCode")}
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.tare")}
                required={needs("tareKg")}
                hint={t("lineEdit.hints.tare")}
              >
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  value={draft.tareKg}
                  onChange={(e) => set("tareKg", e.target.value)}
                  disabled={frozen("tareKg")}
                  placeholder={t("lineEdit.placeholders.kg")}
                />
              </FormField>

              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.maxPayload")}
                required={needs("maxPayloadKg")}
                hint={t("lineEdit.hints.maxPayload")}
              >
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  value={draft.maxPayloadKg}
                  onChange={(e) => set("maxPayloadKg", e.target.value)}
                  disabled={frozen("maxPayloadKg")}
                  placeholder={t("lineEdit.placeholders.kg")}
                  // The payload is never the field reported against — it is a
                  // rating, and what breaches it is the load — but it is half of
                  // both messages, so it goes red with them. The plate's own
                  // contradiction names it too, for the same reason.
                  error={!!vgmIssue || !!cargoIssue || !!plateIssue}
                />
              </FormField>


              {/* ── What the load weighs ─────────────────────────────────
                  A box again, and not the read-only total it briefly was. That
                  total was the right answer for a container described row by
                  row and the only answer available for every other kind: a load
                  arrives at a weighbridge as a single figure, a driver is handed
                  a ticket rather than a spreadsheet, and a great many bookings
                  never itemise at all. Deriving it meant the platform could
                  only hold a weight it had been told how to add up.

                  So this is the declaration and the packing list is the
                  corroboration. Left empty, the rows answer exactly as before —
                  which is why no stored container changes meaning. Typed, it is
                  the figure the VGM rests on, and the rows still have to fit
                  under it.

                  Deliberately *not* the VGM's twin. The VGM stays derived
                  because it is a sum this form can work out; this is a
                  measurement somebody took, and no arithmetic can produce it. */}
              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.cargoWeight")}
                hint={
                  declaredCargoKg === null
                    ? t("lineEdit.hints.cargoFromItems", {
                        listed: Math.round(listedCargoKg).toLocaleString(),
                      })
                    : t("lineEdit.hints.cargoDeclared", {
                        listed: Math.round(listedCargoKg).toLocaleString(),
                      })
                }
                error={issueText(cargoIssue)}
              >
                {/* The placeholder carries the packing list's own total, so an
                    empty box shows what leaving it empty will mean rather than
                    looking like a figure nobody has got round to. */}
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  value={draft.cargoWeightKg}
                  onChange={(e) => set("cargoWeightKg", e.target.value)}
                  disabled={disabled}
                  placeholder={String(Math.round(listedCargoKg))}
                  error={!!cargoIssue}
                />
              </FormField>

              {/* ── The VGM, worked out rather than typed ────────────────
                  This was an input, and it was the one box on the form nobody
                  filling it in was in a position to answer: a dispatcher at a
                  desk cannot know what a container weighs. The hint underneath
                  it already printed the right figure — "the packing list
                  implies N kg (tare + cargo)" — while the box above accepted
                  anything at all, which is a form telling its user the answer
                  and then asking them to guess.

                  So the hint became the value. SOLAS Method 2: weigh what goes
                  inside, add the plated tare. The server derives the same sum
                  on save and is the only writer, so there is nothing here to
                  post — this reads the draft the dispatcher is editing, which
                  is what makes the figure move as the packing list is typed
                  rather than after the save. */}
              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.vgm")}
                hint={t("lineEdit.hints.vgmDerived")}
              >
                <div
                  className="flex h-8 items-center justify-between rounded-md border border-dashed border-[#D8D6CE] bg-[#FAFAF8] px-2.5 text-sm"
                  // Read out as one value rather than as two elements, so it is
                  // not announced as a number followed by an orphaned sum.
                  aria-label={t("lineEdit.fields.vgm")}
                >
                  <span className="font-medium text-gray-900">
                    {derivedVgm === null
                      ? "—"
                      : `${derivedVgm.toLocaleString()} kg`}
                  </span>
                  {/* The sum in the open. A figure nobody can edit has to show
                      its working, or the only way to change it is to guess
                      which of the fields above it came from. */}
                  <span className="text-[11px] text-[#8A9590]">
                    {derivedVgm === null
                      ? t("lineEdit.hints.vgmNeedsTare")
                      : t("lineEdit.hints.vgmSum", {
                          tare: (tare ?? 0).toLocaleString(),
                          cargo: Math.round(cargoKg).toLocaleString(),
                        })}
                  </span>
                </div>
              </FormField>

              {/* Optional, and open for the whole run: the seal goes on at
                  loading and its number often reaches the office afterwards,
                  so this is the one identifier that has to stay fillable after
                  the wheels turn. The driver's screen no longer asks for it. */}
              <FormField
                layout="inline"
                size="sm"
                label={t("lineEdit.fields.sealNumber")}
              >
                <Input
                  size="sm"
                  value={draft.sealNumber}
                  onChange={(e) => set("sealNumber", e.target.value.toUpperCase())}
                  disabled={disabled}
                  placeholder={t("lineEdit.placeholders.sealNumber")}
                />
              </FormField>

            </div>
          </SectionCard>

          <div className="space-y-3">
            <SectionCard icon={Truck} title={t("lineEdit.sections.assignment")}>
              <div className="space-y-1">
                {/* The picker works the way the terminal and the client do two
                    sections down: the selection is the value and the typed text
                    is only ever a search term, discarded when the list closes.
                    So the id and the name it displays cannot drift apart, and an
                    unassignment is the X — never an emptied box.

                    A started container may be moved to another truck, but not
                    left on none: the clear goes away rather than the field,
                    which is the same rule the API holds — a container with no
                    truck is on nobody's screen.

                    Changing it mid-run is a hand-over, and it takes the board
                    position with it: the model clears `planning` whenever the
                    truck moves without a new position being stated, so the
                    container lands back in the waiting pool for the dispatcher
                    to give it a slot on the new truck. The board accepts a
                    started container for exactly that reason.

                    There was a driver picker above this one, and it is gone
                    rather than disabled. Containers are dispatched to a truck
                    and a driver reaches them by signing in on that truck's
                    screen, so naming a person here would have been a guess at a
                    rota that nothing downstream reads. Who actually took the
                    box is shown below, as a fact rather than a field. */}
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("lineEdit.fields.vehicle")}
                  hint={t(
                    started
                      ? "lineEdit.hints.vehicleHandover"
                      : "lineEdit.hints.pickToAssign",
                  )}
                  required={needs("vehicle")}
                >
                  <AssignmentSelect
                    source="trucks"
                    size="sm"
                    selectedId={draft.vehicleId}
                    value={draft.vehicleLabel}
                    onSelect={(pick) =>
                      setDraft((d) =>
                        d
                          ? {
                              ...d,
                              vehicleId: pick.id,
                              vehicleLabel: pick.label,
                              vehiclePlate: pick.plate ?? "",
                            }
                          : d,
                      )
                    }
                    onClear={() =>
                      setDraft((d) =>
                        d
                          ? { ...d, vehicleId: null, vehicleLabel: "", vehiclePlate: "" }
                          : d,
                      )
                    }
                    allowClear={!started}
                    disabled={frozen("vehicle")}
                    placeholder={t("lineEdit.placeholders.vehicle")}
                  />
                </FormField>

                {/*
                  Who has the box, read-only.

                  Written by whoever records a step, not by this form — so it is
                  shown as a line of text rather than as a disabled input, which
                  would suggest a field somebody could be given permission to
                  edit. Absent until the first step, and on a container the
                  office drove to completion itself it stays absent, which is the
                  honest answer: nobody signed in to a truck for it.
                */}
                {draft.driverLabel && (
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("lineEdit.fields.driver")}
                  >
                    <p className="py-1.5 text-sm text-[#1A1A18]">
                      {draft.driverLabel}
                    </p>
                  </FormField>
                )}
              </div>
            </SectionCard>

            {/* Booking — the sailing this box is on. The cut-off used to sit
                here; it now lives in the drop-off block, beside the terminal
                that enforces it. */}
            <SectionCard icon={Ship} title={t("lineEdit.sections.booking")}>
              <div className="space-y-1">
                <FormField layout="inline" size="sm" label={t("labels.shippingLine")}>
                  <Input
                    size="sm"
                    value={draft.shippingLine}
                    onChange={(e) => set("shippingLine", e.target.value)}
                    disabled={disabled}
                    placeholder={t("placeholders.shippingLine")}
                  />
                </FormField>

                <FormField layout="inline" size="sm" label={t("labels.vesselVoyage")}>
                  <div className="grid grid-cols-1 gap-2 @sm/control:grid-cols-2">
                    <Input
                      size="sm"
                      value={draft.vesselName}
                      onChange={(e) => set("vesselName", e.target.value)}
                      disabled={disabled}
                      placeholder={t("placeholders.vessel")}
                    />
                    <Input
                      size="sm"
                      value={draft.voyageNumber}
                      onChange={(e) => set("voyageNumber", e.target.value)}
                      disabled={disabled}
                      placeholder={t("placeholders.voyage")}
                    />
                  </div>
                </FormField>

                {/* Inherited from the order, then this box's own — one booking
                    can be split across several BLs. */}
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("labels.blNumber")}
                  hint={t("hints.blNumberLine")}
                >
                  <Input
                    size="sm"
                    value={draft.blNumber}
                    onChange={(e) =>
                      set("blNumber", e.target.value.toUpperCase())
                    }
                    disabled={disabled}
                    placeholder={t("placeholders.blNumber")}
                  />
                </FormField>

                <FormField
                  layout="inline"
                  size="sm"
                  label={t("lineEdit.fields.bookingRef")}
                  hint={t("lineEdit.hints.containerRef")}
                >
                  <Input
                    size="sm"
                    value={draft.bookingRef}
                    onChange={(e) =>
                      set("bookingRef", e.target.value.toUpperCase())
                    }
                    disabled={disabled}
                    className="font-mono placeholder:font-sans"
                    placeholder={t("placeholders.stopRef")}
                  />
                </FormField>
              </div>
            </SectionCard>

            {/* ── Customs ────────────────────────────────────────────────────
                Its own block rather than a tenth row under Booking, and the
                distinction is not filing tidiness: the booking block describes
                the *sailing* this box is on — a carrier's line, vessel, voyage
                and reference — while an MRN is a national customs system's
                answer about the *goods*. They are issued by different parties,
                arrive at different moments, and are quoted at different
                barriers. A card of one field is honest about that, and is where
                the rest of the declaration goes when there is more of it.

                Open for the whole run and never frozen. The declaration is
                usually lodged before departure and is routinely amended or
                reissued at a customs office partway along the route — which is
                why the driver's own screen writes to this same field. */}
            <SectionCard icon={Stamp} title={t("lineEdit.sections.customs")}>
              <div className="space-y-1">
                <FormField
                  layout="inline"
                  size="sm"
                  label={t("lineEdit.fields.mrnNumber")}
                  hint={t("lineEdit.hints.mrnNumber")}
                >
                  <Input
                    size="sm"
                    value={draft.mrnNumber}
                    onChange={(e) =>
                      set("mrnNumber", e.target.value.toUpperCase())
                    }
                    disabled={disabled}
                    className="font-mono uppercase placeholder:font-sans placeholder:normal-case"
                    placeholder={t("lineEdit.placeholders.mrnNumber")}
                  />
                </FormField>
              </div>
            </SectionCard>

            {/* ── How this box has to be handled ──────────────────────────────
                A block of its own, under the booking, rather than three more
                rows at the bottom of the container's details. It is not a
                property of the box the way its tare and its seal are — it is a
                property of what is *inside* it, and it is the one thing on this
                page that changes how the job is driven rather than how it is
                recorded. Buried under eight weights and numbers, ADR read as one
                more field to fill in.

                Editable for as long as the container is — unlike the box's
                number, none of this is settled by the run starting. A reefer's
                set point is routinely corrected after the box is collected, and
                the driver has to be told. */}
            <SectionCard
              icon={AlertTriangle}
              title={t("lineEdit.sections.handling")}
            >
              <div className="space-y-1">
                <FormField layout="inline" size="sm" label={t("labels.handling")}>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1">
                    {handlingCheckbox(
                      "temperatureSensitive",
                      t("handling.temperature"),
                    )}
                    {handlingCheckbox("adr", t("handling.adr"))}
                    {handlingCheckbox("fragile", t("handling.fragile"))}
                  </div>
                </FormField>

                {/* Each detail appears only while its flag is on, and unchecking
                    clears it — the same pairing the order form uses, so a
                    container and the batch it belongs to ask about cargo
                    handling in exactly the same terms. */}
                {draft.handling.temperatureSensitive && (
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("labels.tempRange")}
                  >
                    <Input
                      size="sm"
                      value={draft.handling.temperatureRange}
                      onChange={(e) =>
                        setHandling({ temperatureRange: e.target.value })
                      }
                      disabled={disabled}
                      placeholder={t("placeholders.tempRange")}
                    />
                  </FormField>
                )}

                {draft.handling.adr && (
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("labels.imoClass")}
                  >
                    <Select
                      size="sm"
                      value={draft.handling.imoClass}
                      onChange={(e) => setHandling({ imoClass: e.target.value })}
                      options={IMO_CLASS_OPTIONS}
                      disabled={disabled}
                      placeholder={t("placeholders.selectImoClass")}
                    />
                  </FormField>
                )}

                {draft.handling.fragile && (
                  <FormField
                    layout="inline"
                    size="sm"
                    label={t("labels.specifications")}
                  >
                    <Textarea
                      size="sm"
                      rows={2}
                      value={draft.handling.specifications}
                      onChange={(e) =>
                        setHandling({ specifications: e.target.value })
                      }
                      disabled={disabled}
                      placeholder={t("placeholders.specifications")}
                    />
                  </FormField>
                )}
              </div>
            </SectionCard>
          </div>
        </div>

        {/* ── The run itself ─────────────────────────────────────────────── */}
        <SectionCard
          icon={MapIcon}
          title={t("sections.routeTerminals")}
          note={<p className="text-[11px]">{t("linePage.routeHint")}</p>}
          /* ── "+ Add stop" ──────────────────────────────────────────────
             In the section header rather than at the foot of the route: on a
             route of eight cards the button was below the fold, and the one
             control that changes the shape of the route should be where the
             section is named. A new stop still lands at the end and is walked
             into place with the arrows — hence the hint, kept on the button
             rather than spelled out beside it.

             Hidden rather than disabled once the route is full — a ceiling
             nobody is near is not worth explaining, and the sentence that
             replaces it says what it is when they are. */
          actions={
            disabled ? undefined : draft.routeStops.length < MAX_ROUTE_STOPS ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                title={t("route.addStopHint")}
                onClick={() => addStopAfter(draft.routeStops.length - 1)}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {t("route.addStop")}
              </Button>
            ) : (
              <span className="text-[11px] text-mist">
                {t("route.stopLimit", { max: MAX_ROUTE_STOPS })}
              </span>
            )
          }
        >
          {/* ── The route, in the order it is driven ────────────────────────
              A list rather than three fixed cards. Three of these stops are the
              anchors the module is named after and are always here; the rest
              are whatever this particular move turned out to need — a customs
              office, a weighbridge, a second loading site, a mandated rest.

              Laid out in a grid that wraps, and numbered, so the order is
              legible at a glance on a route of eight as well as of three. The
              cards themselves carry the controls that change the route, because
              every one of those controls is about *this* stop's place in it. */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {draft.routeStops.map((stop, index) => {
              const slots = STOP_SLOT_KEYS[stop.role] ?? STOP_SLOT_KEYS.custom;
              // Each control is offered exactly when pressing it would be
              // accepted — asked of the same rule the API enforces, rather than
              // of a position, so an unused stop is never mistaken for one the
              // driver has been to.
              const visited = isStopVisited(stop);
              const behindDriver = isStopBehindDriver(draft.routeStops, index);
              const planned = !visited && !behindDriver;

              return (
                <ContainerLineStopCard
                  key={stop._id}
                  stop={stop}
                  index={index + 1}
                  onChange={(patch) => patchStop(index, patch)}
                  disabled={disabled}
                  countries={countries}
                  apiKey={GOOGLE_PLACES_API_KEY}
                  refLabel={t(slots.ref)}
                  dateLabel={t(slots.date)}
                  timeLabel={t(slots.time)}
                  dateError={slotErrors[`${index}.date`]}
                  timeError={slotErrors[`${index}.time`]}
                  // Translated here rather than in the card: the keys are the
                  // order form's, and `translateValidation` is what turns those
                  // into sentences everywhere else on this page.
                  issues={
                    showStopErrors && stopIssues
                      ? translateIssues(stopIssues[String(stop._id)] ?? {})
                      : undefined
                  }
                  visited={visited}
                  // A stop can only swap with a neighbour that is also still
                  // planned, so the first movable stop has no "up" and the last
                  // stop has no "down" — rather than arrows that do nothing.
                  onMoveUp={
                    canMoveStop(draft.routeStops, index, -1)
                      ? () => setRoute((stops) => moveStop(stops, index, -1))
                      : undefined
                  }
                  onMoveDown={
                    canMoveStop(draft.routeStops, index, 1)
                      ? () => setRoute((stops) => moveStop(stops, index, 1))
                      : undefined
                  }
                  // Only added stops can be removed at all: the three anchors
                  // are the route's shape, and one that is not wanted is turned
                  // off with a reason rather than deleted without one.
                  onRemove={
                    planned && stop.role === "custom" && !visited
                      ? () => removeStop(index)
                      : undefined
                  }
                  /**
                   * Two different reasons a stop cannot be rearranged, and they
                   * must not be told as one.
                   *
                   * "The driver has already been here" is true of a visited
                   * stop. It was also being shown on a stop marked *not used*
                   * that happened to sit before the driver — a stop nobody has
                   * ever been to, or ever will — which is simply a false
                   * statement about the container, and the kind that makes a
                   * user distrust everything else on the page.
                   */
                  lockedReason={
                    visited
                      ? t("route.stopVisited")
                      : !planned
                        ? t("route.stopBehindDriver")
                        : undefined
                  }
                >
                  {stop.role === "terminalDropoff" &&
                    stop.enabled !== false &&
                    cutOffFields}
                </ContainerLineStopCard>
              );
            })}
          </div>

          {/* The cut-off with nowhere to live, because the box is not going
              back to a terminal. Still shown: it is the deadline the whole run
              answers to, and the batch list has a column for it. */}
          {cutOffHomeless && (
            <div className="mt-3 max-w-md space-y-1 border-t border-[rgba(11,15,14,0.06)] pt-3">
              <p className="text-[11px] text-mist">{t("route.cutOffOrphan")}</p>
              {cutOffFields}
            </div>
          )}
        </SectionCard>

        {/* What is in the box. Editable while the run is under way on purpose —
            the packing list is finalised at the loading site, stop 2 of 3. */}
        <ContainerLineCargoSection
          rows={draft.cargo}
          onChange={(cargo) => set("cargo", cargo)}
          disabled={disabled}
          showErrors={showCargoErrors}
          allowanceKg={allowance}
          // Decided once, above, and said in both places it matters: beside the
          // weights that set the ceiling, and over the table that has to fit
          // under it.
          weightIssue={cargoIssue ?? null}
        />

        <SectionCard icon={StickyNote} title={t("lineEdit.sections.notes")}>
          <Textarea
            size="sm"
            rows={3}
            value={draft.notes}
            onChange={(e) => set("notes", e.target.value)}
            disabled={disabled}
            placeholder={t("lineEdit.placeholders.notes")}
          />
        </SectionCard>
          </>
        )}

        {/* ── What the cab and the office have said about this box ───────────
            Outside the read/edit switch, like the charges below it: a
            conversation is not a field on this form, and it must stay readable
            and answerable while somebody is mid-edit — a driver at a gate
            waiting on a booking reference should not have to wait for a
            dispatcher to press Save.

            Present on a completed container too. The questions a finished run
            generates arrive after it finishes, and a thread that shut with the
            last step would send those to the telephone. */}
        {containerId && (
          <ContainerMessagesSection
            orderId={orderId}
            lineId={containerId}
            cancelled={line?.status === "cancelled"}
            // Who is actually on the box, off the populated container. Both are
            // read-only here — the driver is observed rather than assigned, and
            // the truck is set on the form above.
            driverName={
              line?.driverId && typeof line.driverId === "object"
                ? `${line.driverId.firstName ?? ""} ${line.driverId.lastName ?? ""}`.trim()
                : null
            }
            // And who is on it in the office, so whoever opens the thread from
            // a company-wide notification knows whose container they have just
            // picked up — see `handler` on the section's props.
            handler={line?.handlerId ?? null}
            truck={
              line?.vehicleId && typeof line.vehicleId === "object"
                ? vehicleLabel(line.vehicleId)
                : null
            }
          />
        )}

        {/* ── What this container is billed for, and what it cost ────────────
            Outside the read/edit switch, and that is deliberate rather than
            convenient. These two tables are the same components the order and
            the shipment use, and they already know how to present themselves
            read-only — so one instance serves both modes, and the invoiced rows
            stay visible to someone who only came to check what was charged.

            It also gives the completed container somewhere to go. The rest of
            the page is shut once a run finishes, because it is a record by then;
            invoicing, however, happens *after* the run, so the billable table
            switches into post-completion mode and manages its rows through their
            own endpoints while everything around it stays locked.

            Charges are inherited from the order when the container is generated
            and are the container's own from that moment — the box that needed an
            extra lift is charged for the lift, and its neighbours are not. */}
        <ServicesSection<ServicesDraft>
          title={t("sections.invoicingServices")}
          // No driver writes a service onto a container: the driver's own
          // screen records steps, weights, the seal and documents, and nothing
          // else. Every row here is the office's, so the badge saying so is a
          // column spent on a constant.
          hideByColumn
          register={chargesForm.register}
          errors={chargesForm.formState.errors}
          readonly={disabled}
          serviceFields={serviceFields}
          appendService={appendService}
          removeService={removeService}
          totalForEachRow={serviceRows}
          grandTotal={servicesTotal}
          services={serviceCatalogue}
          setValue={chargesForm.setValue}
          control={chargesForm.control}
          // The one place the form cannot reach. Offered only on a completed
          // container: while it is still running the ordinary Save does this,
          // and two ways to add the same row would be one too many.
          postCompletionMode={completed}
          onApiAddService={completed ? addServiceAfterCompletion : undefined}
          onApiDeleteService={completed ? removeServiceAfterCompletion : undefined}
        />

        {/* ── What this container has been billed on ────────────────────────
            Directly under the billable charges, because it is the answer to the
            question those rows raise: an invoiced row shows a lock and no bin,
            and this is where you find out which document did that.

            Only once there is something to show — the component renders nothing
            when a container has never been invoiced, which is most of them for
            most of their life. */}
        <InvoiceRefs containerLineId={containerId} />

        <CostServicesSection<ServicesDraft>
          register={chargesForm.register}
          errors={chargesForm.formState.errors}
          readonly={disabled}
          costServiceFields={costServiceFields}
          appendCostService={appendCostService}
          removeCostService={removeCostService}
          totalForEachRow={costRows}
          grandTotal={costTotal}
          services={costServiceCatalogue}
          setValue={chargesForm.setValue}
          control={chargesForm.control}
          // The cost side needs the same door as the billable side, and for a
          // reason that is if anything plainer: a supplier's charge for a lift
          // or a waiting hour arrives *after* the box is back and the container
          // is a closed record. Refusing to record what a finished job cost is
          // not bookkeeping, it is a hole in it.
          postCompletionMode={completed}
          onApiAddService={completed ? addCostAfterCompletion : undefined}
          onApiDeleteService={completed ? removeCostAfterCompletion : undefined}
        />

        {/* ── Documents ──────────────────────────────────────────────────────
            Outside the read/edit switch, like the order block at the top: the
            list reads the same in both modes and a document is worth opening
            from a page someone is only checking. Read mode simply offers no way
            to add or remove one.

            Both sides file into this list — the office here, the driver from
            their own screen — and each row says which. Removing another side's
            document is refused by the API; the button is not offered for it. */}
        <ContainerLineAttachmentsSection
          attachments={attachments}
          pendingGeneral={pendingGeneral}
          pendingCustoms={pendingCustoms}
          onPendingChange={setPending}
          onDownload={downloadAttachment}
          onDelete={deleteAttachment}
          busyId={busyAttachment}
          readonly={disabled}
          viewer={{ userId: authUser?._id, isDriver: false }}
        />

        {/* No Save down here: the header sits above the scroll area and never
            moves, so the one up there is already within reach from the bottom
            of the page. Two of them only raise the question of whether they do
            the same thing. */}
        <div className="flex items-center justify-end pt-1">
          <Button type="button" variant="outline" onClick={backToOrder}>
            {t("linePage.backToOrder")}
          </Button>
        </div>
              </div>
            </div>

              {/* Railed inside the left pane, against its right edge — where
                  the note's own rail sits on the far side of it, so the two
                  stand together at every width instead of drifting apart as the
                  window grows. Sticky against that pane, so "where has this box
                  got to" stays answered at every point of a form that runs long
                  in both modes; `top-4` matches the pane's own top padding, and
                  the rail scrolls within itself if a run with a note on every
                  step outgrows the viewport.

                  Collapses exactly as the consignment note does, down to a
                  labelled spine rather than to nothing: a reference that
                  vanishes reads as a feature the page does not have. Closed on
                  arrival, so the form gets the width until someone asks for the
                  journey.

                  The divider belongs to the rail, not to the form: the form is
                  centred in what is left over, so a rule on *its* edge would
                  float in open space. Closed, the spine carries its own border
                  and a second one beside it divides nothing. */}
              <aside
                className={`hidden shrink-0 border-[rgba(11,15,14,0.08)] transition-[width] duration-300 xl:block ${
                  timelineCollapsed
                    ? "w-10 pl-2"
                    : "w-[240px] border-l pl-6"
                }`}
              >
                {/* Sticky, and the handle lives *inside* the sticky box.

                    It used to be positioned against the `<aside>` itself, which
                    does not stick — so the control for the rail scrolled up out
                    of the page while the rail it controls stayed put. Anything
                    pinned here has to be pinned to the same element the rail is
                    pinned to. `top-4` matches the pane's own top padding. */}
                <div className="sticky top-4 max-h-[calc(100vh-8rem)]">
                  {timelineCollapsed ? (
                    /* Closed: the whole spine is the button.

                       A floating chevron next to a grey strip does not say what
                       the strip is or that it opens — this says both. The label
                       reads downwards from them (`vertical-rl`), the chevron
                       sits at the top pointing into the page, and the whole
                       thing lifts on hover.

                       The label is the short one — "Timeline", not "Container
                       timeline". Vertical text spends height on every
                       character, and the long form made a 180px strip out of a
                       word that had already been established by the page it is
                       on. The note's spine says "CMR" for the same reason, and
                       the two now stand the same height. */
                    <button
                      type="button"
                      onClick={() => setTimelineCollapsed(false)}
                      className="group flex w-full flex-col items-center gap-2 rounded-lg border border-[rgba(11,15,14,0.08)] bg-bone py-2.5 text-mist transition-colors hover:border-copper/40 hover:bg-white hover:text-copper"
                      title={t("linePage.timeline.expand")}
                      aria-label={t("linePage.timeline.expand")}
                      aria-expanded={false}
                    >
                      <ChevronLeft className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:-translate-x-0.5" />
                      <ListChecks className="h-4 w-4 shrink-0" />
                      <span className="text-[11px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl]">
                        {t("linePage.timeline.spine")}
                      </span>
                    </button>
                  ) : (
                    /* Open: a titled rail with its own close control, so the
                       way out sits where the way in was. The list scrolls
                       inside the sticky box — the header does not go with it. */
                    <div className="flex max-h-[calc(100vh-8rem)] flex-col">
                      <div className="mb-2 flex shrink-0 items-center justify-between gap-1 px-1">
                        <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-mist">
                          <ListChecks className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            {t("linePage.timeline.title")}
                          </span>
                        </span>
                        {/* The note's close control exactly: a bordered disc
                            with a shadow, not a bare glyph. A chevron drawn
                            straight onto the page reads as decoration — the
                            frame is what says "this is a button, press it and
                            the rail goes away". */}
                        <button
                          type="button"
                          onClick={() => setTimelineCollapsed(true)}
                          className="shrink-0 rounded-full border border-gray-300 bg-white p-1 text-mist shadow-sm transition-colors hover:border-copper/40 hover:bg-bone hover:text-copper"
                          title={t("linePage.timeline.collapse")}
                          aria-label={t("linePage.timeline.collapse")}
                          aria-expanded
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
                        {/* The live route, not the stored one — see
                            `timelineLine`. The component prints its own title,
                            which the rail header now carries instead. */}
                        <ContainerLineTimeline
                          line={timelineLine ?? line}
                          hideTitle
                        />
                      </div>
                    </div>
                  )}
                </div>
              </aside>
          </div>
        </div>

        {/* ── The consignment note ────────────────────────────────────────────
            The right-hand pane, scrolling in its own right, exactly as the
            shipment form's preview does. It is a reference rather than part of
            the form, so it collapses to a tenth of the width instead of
            closing — a rail that disappears entirely reads as a feature that
            is not there — and below `lg` it is not rendered at all, because
            one column cannot hold a form and a document side by side.

            Everything on it is optional by design. A container is filled in
            over three stops, so the note is drawn off a record that is
            legitimately half-written, and a blank box shows as the blank box
            it is on the paper form rather than blocking the document. */}
        <div
          className={`relative hidden shrink-0 bg-white transition-[width] duration-300 lg:block ${
            cmrCollapsed ? "lg:w-14" : "lg:w-[45%]"
          }`}
        >
          {/* The close control belongs to the open pane only. Closed, the spine
              is the control — see below. */}
          {!cmrCollapsed && (
            <button
              type="button"
              onClick={() => setCmrCollapsed(true)}
              className="absolute -left-3 top-1/2 z-10 -translate-y-1/2 rounded-full border border-gray-300 bg-white p-1.5 shadow-md transition-colors hover:bg-gray-50"
              title={t("lineCmr.collapse")}
              aria-label={t("lineCmr.collapse")}
              aria-expanded
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}

          <div
            className={`h-full overflow-y-auto overscroll-contain px-2 py-4 ${
              cmrCollapsed ? "overflow-hidden" : ""
            }`}
          >
            {cmrCollapsed ? (
              /* The timeline's spine, mirrored: same 40px strip, same chevron
                 pointing into the page, same hover. Two references either side
                 of the form that close and open the same way and cost the same
                 nothing while closed — it used to hold a tenth of the page
                 open to show an icon. Sticky, so it is reachable from anywhere
                 down a form that runs long. */
              <button
                type="button"
                onClick={() => setCmrCollapsed(false)}
                className="group sticky top-0 flex w-full flex-col items-center gap-2 rounded-lg border border-[rgba(11,15,14,0.08)] bg-bone py-2.5 text-mist transition-colors hover:border-copper/40 hover:bg-white hover:text-copper"
                title={t("lineCmr.expand")}
                aria-label={t("lineCmr.expand")}
                aria-expanded={false}
              >
                <ChevronLeft className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:-translate-x-0.5" />
                <FileText className="h-4 w-4 shrink-0" />
                <span className="text-[11px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl]">
                  {t("lineCmr.spine")}
                </span>
              </button>
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-[12px] font-semibold text-ink">
                    <FileText className="h-3.5 w-3.5" />
                    {t("lineCmr.title")}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadCmr}
                    isLoading={downloadingCmr}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    {t("lineCmr.download")}
                  </Button>
                </div>
                {cmrOrder && (
                  /* The note's own sheet: the shipment preview's exact frame,
                     down to the 90% scale that fits an A4 layout into a 45%
                     pane without reflowing any of its boxes. */
                  <div className="min-h-[600px] origin-top scale-90 border border-gray-300 bg-white p-3 font-mono text-xs">
                    <CmrPreview
                      shipment={cmrOrder}
                      countries={countries}
                      // The container's truck is carried through as its
                      // registration already, so there is no fleet list to
                      // resolve an id against — and a container has no
                      // trailer of its own at all.
                      trucks={[]}
                      trailers={[]}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Portalled out of this form, so its buttons cannot submit the
          container and the sticky header cannot cover it. */}
      {confirmDialog}
    </form>
  );
}

export default ContainerLinePage;
